import axios from 'axios';
import { API_ENDPOINTS } from '../config/api';
import { User, CreateUserRequest, ApiResponse, Farm, CreateFarmRequest, SelfBenefit, CreateSelfBenefitRequest, ReferralMilestone, CreateReferralMilestoneRequest, ReferralConfig, UpdateReferralConfigRequest, RoleChangeRequest, RoleChangeRequestResponse, AssignAdminToFarmRequest, Market, CreateMarketRequest, AdminCreateOrderRequest, Coupon, GenerateCouponsRequest, AssignCouponRequest, ApplyCouponRequest, OrderCouponBlock, SetUnitNomineeRequest, SetUnitNomineesBulkRequest, SetUnitNomineesRequest, UpdateNomineeRequest, OrderUnit, OrderUnitNominee, Cashier } from '../types';
import { getAuthToken, clearAuthToken } from '../utils/authToken';
import { RefundPreview } from '../utils/refundSummary';
import { UserSession } from '../components/common/loginActivity';

/*
const AES_KEY = process.env.REACT_APP_PAYLOAD_AES_KEY || '';

// Backend Decryption AES Key loading
if (!AES_KEY) {
  // Silent warning for production unless critical
}


function wordArrayToUint8Array(wordArray: CryptoJS.lib.WordArray): Uint8Array {
  const words = wordArray.words;
  const sigBytes = wordArray.sigBytes;
  const u8 = new Uint8Array(sigBytes);
  for (let i = 0; i < sigBytes; i++) {
    u8[i] = (words[i >>> 2] >>> (24 - (i % 4) * 8)) & 0xff;
  }
  return u8;
}

function decrypt(encryptedBase64: string) {
  if (!AES_KEY) {
    return null;
  }
  try {
    const raw = CryptoJS.enc.Base64.parse(encryptedBase64);
    const iv = CryptoJS.lib.WordArray.create(raw.words.slice(0, 4), 16);
    const ciphertext = CryptoJS.lib.WordArray.create(raw.words.slice(4), raw.sigBytes - 16);

    const decrypted = CryptoJS.AES.decrypt(
      { ciphertext } as any,
      CryptoJS.enc.Utf8.parse(AES_KEY),
      { iv, mode: CryptoJS.mode.CBC, padding: CryptoJS.pad.Pkcs7 }
    );

    // Backend always gzip-compresses before encrypting — decompress first
    const decompressed = pako.ungzip(wordArrayToUint8Array(decrypted));
    const jsonStr = new TextDecoder().decode(decompressed);

    if (!jsonStr) {
      return null;
    }
    return JSON.parse(jsonStr);
  } catch (error) {
    return null;
  }
}
*/

export const api = axios.create({
  timeout: 30000,
});

// Attach the backend JWT (see utils/authToken.ts) to every request. The
// backend's resolve_caller_mobile prefers this over the legacy x-*-mobile
// headers individual service calls below still send, and requires it
// outright once IS_AUTH_ENABLED is on — those legacy headers alone won't
// authenticate anything at that point.
api.interceptors.request.use((config) => {
  const token = getAuthToken();
  if (token) {
    config.headers = config.headers || {};
    (config.headers as any).Authorization = `Bearer ${token}`;
  }
  return config;
});

// The logged-in staff member, so KYC history records who verified / flagged / edited.
// The backend trusts this header only while token auth is off (utils/auth.resolve_staff_caller).
const staffHeaders = (): Record<string, string> => {
  try {
    const mobile = JSON.parse(window.localStorage.getItem('ak_dashboard_session') || 'null')?.mobile;
    return mobile ? { 'x-caller-mobile': String(mobile) } : {};
  } catch {
    return {};
  }
};

// On a 401 (missing/invalid/expired token), drop the stale token and let the
// app know so it can bounce back to the login screen — a fresh OTP verify is
// the only way to get a new one.
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error?.response?.status === 401) {
      clearAuthToken();
      window.dispatchEvent(new Event('auth:unauthorized'));
    }
    return Promise.reject(error);
  }
);

// Axios interceptor — auto-decrypts every response
/*
api.interceptors.response.use(
  (response) => {
    if (response.data?.payload) {
      const decryptedData = decrypt(response.data.payload);
      if (decryptedData) {
        response.data = decryptedData;
      }
    }
    return response;
  },
  (error) => {
    return Promise.reject(error);
  }
);
*/


// Helper to extract a string error message from various backend response formats
const extractErrorMessage = (error: any): string => {
  if (!error) return 'An unknown error occurred';
  if (typeof error === 'string') return error;

  // If it's already an error object we extracted
  if (error.error && typeof error.error === 'string') return error.error;

  // Handle Axios error structure or direct response data
  const data = error?.response?.data || error;

  if (data) {
    if (data.message) return typeof data.message === 'string' ? data.message : JSON.stringify(data.message);
    if (data.detail) {
      if (Array.isArray(data.detail)) {
        return data.detail.map((d: any) => d.msg || d.message || JSON.stringify(d)).join(', ');
      }
      return typeof data.detail === 'string' ? data.detail : JSON.stringify(data.detail);
    }
    if (data.error) return typeof data.error === 'string' ? data.error : JSON.stringify(data.error);
  }

  if (error.message) return error.message;
  return 'An unexpected error occurred';
};

// A user who can refer a new one (has a referral code) — Create User's referrer search.
export interface ReferrerOption { mobile: string; referral_code: string; role?: string; name?: string }

export const userService = {
  getUsers: async (params?: any): Promise<any> => {
    try {
      const response = await api.get(API_ENDPOINTS.getUsers(), { params });
      return response.data;
    } catch (error) {
      console.error('Error fetching users:', error);
      throw error;
    }
  },

  getReferrals: async (): Promise<User[]> => {
    try {
      const response = await api.get<{ status: string, statuscode: number, users: User[] }>(API_ENDPOINTS.getReferrals());
      return response.data.users;
    } catch (error) {
      console.error('Error fetching referrals:', error);
      throw error;
    }
  },

  getUserDetails: async (mobile: string): Promise<any> => {
    try {
      const response = await api.get<any>(API_ENDPOINTS.getUserDetails(mobile));
      return response.data;
    } catch (error) {
      console.error('Error fetching user details:', error);
      throw error;
    }
  },

  getNetwork: async (params?: any): Promise<any> => {
    try {
      const response = await api.get<any>(API_ENDPOINTS.getReferrals(), { params });
      const data = response.data;
      // Ensure we pass back useful info if structure differs
      // If stats is missing but we have count/total, let's normalize it for the slice/component?
      // Actually, the slice expects { stats, users }. Layout usually matches. 
      // But if it's missing, let's try to polyfill in the slice or here.
      // Returning raw data is fine, slice handles interpretation.
      return data;
    } catch (error) {
      console.error('Error fetching network:', error);
      throw error;
    }
  },

  getNetworkUserDetails: async (mobile: string): Promise<any> => {
    try {
      const response = await api.get<any>(API_ENDPOINTS.getReferrals() + `/${mobile}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching network user details:', error);
      throw error;
    }
  },

  createUser: async (userData: CreateUserRequest): Promise<ApiResponse<any>> => {
    try {
      const response = await api.post(API_ENDPOINTS.createUser(), userData);

      // Check if response indicates an error despite 200 OK (soft error)
      if (response.data && (response.data.status === 'error' || response.data.statuscode >= 400)) {
        return { error: extractErrorMessage(response.data) };
      }

      return { data: response.data, message: 'User created successfully' };
    } catch (error: any) {
      console.error('Error creating user:', error);
      return { error: extractErrorMessage(error) };
    }
  },

  verifyUser: async (mobile: string): Promise<void> => {
    try {
      const response = await api.post(API_ENDPOINTS.verifyUser(), { mobile, device_id: 'web', device_model: 'browser' });
      if (response.status < 200 || response.status >= 300) {
        throw new Error('Failed to verify user');
      }
    } catch (error) {
      console.error('Error verifying user:', error);
      throw error;
    }
  },

  updateUser: async (mobile: string, updates: Partial<User>): Promise<ApiResponse<any>> => {
    try {
      const response = await api.put(API_ENDPOINTS.updateUser(mobile), updates);
      if (response.data && (response.data.status === 'error' || response.data.statuscode >= 400)) {
        return { error: response.data.message || 'Failed to update user' };
      }
      return { data: response.data };
    } catch (error: any) {
      return { error: extractErrorMessage(error) };
    }
  },

  verifyKycFields: async (mobile: string, fields: string[], verified: boolean, values?: Record<string, string>): Promise<ApiResponse<any>> => {
    try {
      const response = await api.post(API_ENDPOINTS.verifyKycFields(mobile), { fields, verified, ...(values ? { values } : {}) }, { headers: staffHeaders() });
      if (response.data && (response.data.status === 'error' || response.data.statuscode >= 400)) {
        return { error: response.data.message || 'Failed to verify KYC fields' };
      }
      return { data: response.data };
    } catch (error: any) {
      return { error: error.response?.data?.message || error.message || 'Failed to verify KYC fields' };
    }
  },

  kycAcknowledgment: async (mobile: string, status: string, remarks?: string): Promise<ApiResponse<any>> => {
    try {
      const response = await api.post(API_ENDPOINTS.kycAcknowledgment(mobile), { status, remarks });
      if (response.data && (response.data.status === 'error' || response.data.statuscode >= 400)) {
        return { error: response.data.message || 'Failed to submit acknowledgment' };
      }
      return { data: response.data };
    } catch (error: any) {
      return { error: error.response?.data?.message || error.message || 'Failed to submit acknowledgment' };
    }
  },
  checkHealth: async (): Promise<boolean> => {
    try {
      await api.get(API_ENDPOINTS.health());
      return true;
    } catch (error) {
      console.error('Health check failed:', error);
      return false;
    }
  },

  getAdmins: async (): Promise<any[]> => {
    try {
      const response = await api.get<{ status: string, statuscode: number, users: any[], count: number }>(API_ENDPOINTS.getAdmins());
      return response.data.users || [];
    } catch (error) {
      console.error('Error fetching admins:', error);
      throw error;
    }
  },

  addAdmin: async (adminData: any, superAdminMobile: string, superAdminOtp: string): Promise<ApiResponse<any>> => {
    try {
      const response = await api.post(API_ENDPOINTS.addAdmin(), adminData, {
        headers: {
          'x-admin-mobile': superAdminMobile,
          'x-admin-otp': superAdminOtp,
          'Content-Type': 'application/json'
        }
      });

      if (response.data && (response.data.status === 'error' || response.data.statuscode >= 400)) {
        return { error: response.data.message || 'Failed to add admin' };
      }

      return { data: response.data, message: 'Admin added successfully' };
    } catch (error: any) {
      console.error('Error adding admin:', error);
      return { error: extractErrorMessage(error) };
    }
  },

  // List Employee/Supervisor/Cashier staff with their screen-access (SuperAdmin only).
  listStaff: async (superAdminMobile: string, params?: { search?: string; role?: string; department_id?: string }): Promise<any> => {
    try {
      const response = await api.get<any>(API_ENDPOINTS.listStaff(), {
        params,
        headers: { 'x-super-admin-mobile': superAdminMobile },
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching staff:', error);
      throw error;
    }
  },

  // Add a staff member (role gated by caller: SuperAdmin/Admin/Supervisor, OTP-gated).
  addStaff: async (
    payload: { mobile: string; first_name: string; last_name?: string; email?: string; role: string; department_id?: string; reporting_to?: string; farm_id?: string },
    callerMobile: string,
    callerOtp: string,
  ): Promise<ApiResponse<any>> => {
    try {
      const response = await api.post(API_ENDPOINTS.addStaff(), payload, {
        headers: { 'x-super-admin-mobile': callerMobile, 'x-super-admin-otp': callerOtp, 'Content-Type': 'application/json' },
      });
      if (response.data && (response.data.status === 'error' || response.data.statuscode >= 400)) {
        return { error: response.data.message || 'Failed to add staff' };
      }
      return { data: response.data, message: 'Staff added' };
    } catch (error: any) {
      console.error('Error adding staff:', error);
      return { error: extractErrorMessage(error) };
    }
  },

  /** Remove ONE role, leaving the member's other roles intact. The backend
   *  refuses with 400 when it is their last role, so the error is surfaced
   *  as-is. */
  revokeRole: async (
    mobile: string,
    role: string,
    adminMobile: string,
    adminOtp: string,
  ): Promise<ApiResponse<any>> => {
    try {
      const response = await api.delete(API_ENDPOINTS.revokeStaffRole(mobile, role), {
        headers: { 'x-admin-mobile': adminMobile, 'x-admin-otp': adminOtp },
      });
      if (response.data && (response.data.status === 'error' || response.data.statuscode >= 400)) {
        return { error: response.data.message || 'Failed to remove role' };
      }
      return { data: response.data, message: 'Role removed' };
    } catch (error: any) {
      console.error('Error revoking role:', error);
      return { error: extractErrorMessage(error) };
    }
  },

  // Replace a staff user's screen-access map (SuperAdmin only).
  setScreenAccess: async (
    mobile: string,
    screenAccess: Record<string, 'read' | 'write' | 'none'>,
    superAdminMobile: string,
    screenActions?: Record<string, string[]>,
    managedScreens?: string[],
  ): Promise<ApiResponse<any>> => {
    try {
      const response = await api.put(
        API_ENDPOINTS.setScreenAccess(mobile),
        {
          screen_access: screenAccess,
          screen_actions: screenActions || {},
          managed_screens: managedScreens,
        },
        { headers: { 'x-super-admin-mobile': superAdminMobile, 'Content-Type': 'application/json' } },
      );
      if (response.data && (response.data.status === 'error' || response.data.statuscode >= 400)) {
        return { error: response.data.message || 'Failed to update screen access' };
      }
      return { data: response.data, message: 'Screen access updated' };
    } catch (error: any) {
      console.error('Error updating screen access:', error);
      return { error: extractErrorMessage(error) };
    }
  },

  /** Move an existing staff member between Marketing Executive / Lead / Manager,
   *  optionally moving a team under them. Applies immediately — no OTP.
   *  Partial success is normal: `team.rejected` names members that could not be
   *  moved, with a reason, while the rest still moved. */
  changeStaffPosition: async (
    mobile: string,
    payload: { role: string; department_id: string; reporting_to?: string; team?: string[] },
    callerMobile: string,
  ): Promise<ApiResponse<any>> => {
    try {
      const response = await api.put(
        API_ENDPOINTS.changeStaffPosition(mobile),
        payload,
        { headers: { 'x-super-admin-mobile': callerMobile, 'Content-Type': 'application/json' } },
      );
      if (response.data && (response.data.status === 'error' || response.data.statuscode >= 400)) {
        return { error: response.data.message || 'Failed to change position' };
      }
      return { data: response.data, message: 'Position updated' };
    } catch (error: any) {
      console.error('Error changing staff position:', error);
      return { error: extractErrorMessage(error) };
    }
  },

  /** Add team members under a staff member who is ALREADY correctly positioned
   *  as a Marketing Executive / Lead / Manager — no position change involved.
   *  Existing reports are kept regardless. Partial success is normal:
   *  `team.rejected` names members that could not be moved, with a reason. */
  addStaffTeam: async (
    mobile: string,
    payload: { department_id: string; team: string[] },
    callerMobile: string,
  ): Promise<ApiResponse<any>> => {
    try {
      const response = await api.post(
        API_ENDPOINTS.addStaffTeam(mobile),
        payload,
        { headers: { 'x-super-admin-mobile': callerMobile, 'Content-Type': 'application/json' } },
      );
      if (response.data && (response.data.status === 'error' || response.data.statuscode >= 400)) {
        return { error: response.data.message || 'Failed to add team members' };
      }
      return { data: response.data, message: 'Team updated' };
    } catch (error: any) {
      console.error('Error adding staff team:', error);
      return { error: extractErrorMessage(error) };
    }
  },

  /** Return the direct and indirect reports below the caller in the STAFF_UNDER tree.
   *  `callerMobile` is the x-caller-mobile; `callerUserId` is the opaque user_id used
   *  as `department_head_id` for the backend lookup (falls back to mobile). */
  getMyTeam: async (callerMobile: string, callerUserId?: string): Promise<any> => {
    try {
      const headId = callerUserId || callerMobile;
      const response = await api.get<any>(API_ENDPOINTS.myTeam(headId));
      return response.data;
    } catch (error) {
      console.error('Error fetching team:', error);
      return { team: [] };
    }
  },

  /** List all departments (name + member counts + roles) for the Staff Access page and
   *  the Add Staff / Create Department dropdowns. No auth header required. */
  listDepartments: async (): Promise<{
    statuscode: number;
    status: string;
    departments: Array<{
      dept_id: string | null;
      name: string;
      head_mobile: string | null;
      head_name?: string | null;
      head_role?: string | null;
      members: number;
      roles?: Record<string, number>;
      custom_roles?: string[];
      dept_roles?: string[];
      role_hierarchy?: Record<string, string>;
    }>;
  }> => {
    try {
      const response = await api.get<any>(API_ENDPOINTS.listDepartments());
      return response.data;
    } catch (error) {
      console.error('Error fetching departments:', error);
      return { statuscode: 500, status: 'error', departments: [] };
    }
  },

  /** Get department details, role breakdown, and members (filtered by role optional) */
  getDepartmentMembers: async (
    deptId: string,
    role?: string
  ): Promise<{
    statuscode: number;
    status: string;
    department?: {
      dept_id: string;
      name: string;
      head_mobile: string | null;
      head_name?: string | null;
      head_role?: string | null;
      custom_roles?: string[];
      dept_roles?: string[];
      role_hierarchy?: Record<string, string>;
      created_at?: string;
      updated_at?: string;
    };
    role_filter?: string;
    members: Array<{
      mobile: string;
      user_id?: string;
      first_name?: string;
      last_name?: string;
      role?: string;
      email?: string;
    }>;
    member_count?: number;
    roles?: Record<string, number>;
  }> => {
    try {
      const response = await api.get<any>(API_ENDPOINTS.getDepartmentMembers(deptId, role));
      return response.data;
    } catch (error) {
      console.error('Error fetching department members:', error);
      return { statuscode: 500, status: 'error', members: [] };
    }
  },

  /** Create a department, optionally seeding its role catalog in one call.
   *  `roles` is applied IN ORDER, so a role may report to one listed before it.
   *  head_mobile is still never sent from here: a head has to exist as a user
   *  first, so heads are assigned afterwards via assignDepartmentHead. */
  createDepartment: async (
    name: string,
    adminMobile: string,
    adminOtp: string,
    roles?: Array<{ role_name: string; reports_to_role?: string }>,
  ): Promise<ApiResponse<any>> => {
    try {
      const body: Record<string, any> = { name };
      if (roles?.length) body.roles = roles;
      const response = await api.post(API_ENDPOINTS.createDepartment(), body, {
        headers: { 'x-admin-mobile': adminMobile, 'x-admin-otp': adminOtp, 'Content-Type': 'application/json' },
      });
      if (response.data && (response.data.status === 'error' || response.data.statuscode >= 400)) {
        return { error: response.data.message || 'Failed to create department' };
      }
      return { data: response.data, message: 'Department created' };
    } catch (error: any) {
      console.error('Error creating department:', error);
      return { error: extractErrorMessage(error) };
    }
  },

  /** Create a custom role under a department (Admin/SuperAdmin, OTP-gated).
   *  `reportsToRole` names another of this department's existing roles that
   *  staff with the new role will auto-report to (via STAFF_UNDER) when there's
   *  exactly one person currently holding it. */
  createDepartmentRole: async (deptId: string, roleName: string, adminMobile: string, adminOtp: string, reportsToRole?: string): Promise<ApiResponse<any>> => {
    try {
      const response = await api.post(API_ENDPOINTS.createDepartmentRole(deptId), { role_name: roleName, reports_to_role: reportsToRole || undefined }, {
        headers: { 'x-admin-mobile': adminMobile, 'x-admin-otp': adminOtp, 'Content-Type': 'application/json' },
      });
      if (response.data && (response.data.status === 'error' || response.data.statuscode >= 400)) {
        return { error: response.data.message || 'Failed to create role' };
      }
      return { data: response.data, message: 'Role created' };
    } catch (error: any) {
      console.error('Error creating department role:', error);
      return { error: extractErrorMessage(error) };
    }
  },

  /** Set or REPLACE a department's head, preserving the role they already hold.
   *  `headRole` is optional and additive — omit it and the user's roles are left
   *  untouched (this is what stops a Farm Manager being relabelled). With
   *  `rewireReports`, department members whose role reports to the head's role
   *  but who have no reporting link yet are attached under the new head. */
  assignDepartmentHead: async (
    deptId: string,
    headMobile: string,
    adminMobile: string,
    adminOtp: string,
    headRole?: string,
    rewireReports: boolean = true,
  ): Promise<ApiResponse<any>> => {
    try {
      const response = await api.post(
        API_ENDPOINTS.assignDepartmentHead(deptId),
        { head_mobile: headMobile, head_role: headRole || undefined, rewire_reports: rewireReports },
        { headers: { 'x-admin-mobile': adminMobile, 'x-admin-otp': adminOtp, 'Content-Type': 'application/json' } },
      );
      if (response.data && (response.data.status === 'error' || response.data.statuscode >= 400)) {
        return { error: response.data.message || 'Failed to assign head' };
      }
      return { data: response.data, message: 'Department head updated' };
    } catch (error: any) {
      console.error('Error assigning department head:', error);
      return { error: extractErrorMessage(error) };
    }
  },

  /** Set which role an existing department role reports to (or clear it with
   *  `undefined`). POST /roles only accepts reports_to_role for a brand-new
   *  role, so this is the only way to wire up a department whose roles already
   *  exist. The backend rejects a self-report and any cycle with 400. */
  updateDepartmentRoleReportsTo: async (
    deptId: string,
    roleName: string,
    reportsToRole: string | undefined,
    adminMobile: string,
    adminOtp: string,
  ): Promise<ApiResponse<any>> => {
    try {
      const response = await api.put(
        API_ENDPOINTS.updateDepartmentRole(deptId, roleName),
        { reports_to_role: reportsToRole || null },
        { headers: { 'x-admin-mobile': adminMobile, 'x-admin-otp': adminOtp, 'Content-Type': 'application/json' } },
      );
      if (response.data && (response.data.status === 'error' || response.data.statuscode >= 400)) {
        return { error: response.data.message || 'Failed to update role' };
      }
      return { data: response.data, message: 'Reporting line updated' };
    } catch (error: any) {
      console.error('Error updating department role:', error);
      return { error: extractErrorMessage(error) };
    }
  },

  /** Remove a role from a department's catalog. The backend refuses with 409
   *  while any member still holds it, so the error is surfaced as-is. */
  deleteDepartmentRole: async (
    deptId: string,
    roleName: string,
    adminMobile: string,
    adminOtp: string,
  ): Promise<ApiResponse<any>> => {
    try {
      const response = await api.delete(API_ENDPOINTS.deleteDepartmentRole(deptId, roleName), {
        headers: { 'x-admin-mobile': adminMobile, 'x-admin-otp': adminOtp },
      });
      if (response.data && (response.data.status === 'error' || response.data.statuscode >= 400)) {
        return { error: response.data.message || 'Failed to delete role' };
      }
      return { data: response.data, message: 'Role deleted' };
    } catch (error: any) {
      console.error('Error deleting department role:', error);
      return { error: extractErrorMessage(error) };
    }
  },

  /** Delete a department. `force` is required when it still has members; their
   *  MEMBER_OF edges are detached but the users themselves keep their role and
   *  are never deleted. */
  deleteDepartment: async (
    deptId: string,
    adminMobile: string,
    adminOtp: string,
    force: boolean = false,
  ): Promise<ApiResponse<any>> => {
    try {
      const response = await api.delete(API_ENDPOINTS.deleteDepartment(deptId, force), {
        headers: { 'x-admin-mobile': adminMobile, 'x-admin-otp': adminOtp },
      });
      if (response.data && (response.data.status === 'error' || response.data.statuscode >= 400)) {
        return { error: response.data.message || 'Failed to delete department' };
      }
      return { data: response.data, message: 'Department deleted' };
    } catch (error: any) {
      console.error('Error deleting department:', error);
      return { error: extractErrorMessage(error) };
    }
  },
};

export const farmManagementService = {
  getRoster: async (callerMobile: string, params: { scope: string; stage?: string; farmId?: string; search?: string; page?: number; page_size?: number }): Promise<any> => {
    try {
      const response = await api.get<any>(API_ENDPOINTS.farmManagementRoster(), {
        params, headers: { 'x-caller-mobile': callerMobile },
      });
      return response.data;
    } catch (error: any) {
      console.error('Error fetching farm-management roster:', error);
      return { error: extractErrorMessage(error) };
    }
  },

  addBuffalo: async (callerMobile: string, data: any): Promise<any> => {
    try {
      const response = await api.post<any>(API_ENDPOINTS.farmManagementProcurementBuffaloes(), data, {
        headers: { 'x-caller-mobile': callerMobile },
      });
      return response.data;
    } catch (error: any) {
      console.error('Error adding buffalo:', error);
      return { error: extractErrorMessage(error) };
    }
  },

  // Markets an animal was procured from — the picker in AddBuffaloModal lists these,
  // and can create a new one on the fly via addProcurementMarket below.
  getProcurementMarkets: async (callerMobile: string): Promise<any> => {
    try {
      const response = await api.get<any>(API_ENDPOINTS.farmManagementProcurementMarkets(), {
        headers: { 'x-caller-mobile': callerMobile },
      });
      return response.data;
    } catch (error: any) {
      console.error('Error fetching procurement markets:', error);
      return { error: extractErrorMessage(error) };
    }
  },

  // Farm Manager / SuperAdmin only — 403s for other roles. Returns `created: false`
  // (still 200) rather than an error if the name already exists.
  addProcurementMarket: async (callerMobile: string, data: { name: string; state?: string; location?: string }): Promise<any> => {
    try {
      const response = await api.post<any>(API_ENDPOINTS.farmManagementProcurementMarkets(), data, {
        headers: { 'x-caller-mobile': callerMobile },
      });
      return response.data;
    } catch (error: any) {
      console.error('Error adding procurement market:', error);
      return { error: extractErrorMessage(error) };
    }
  },

  // Buyers the farm sells to. Employee / Admin / SuperAdmin only — a Farm
  // Manager who can manage the market list is 403'd here, by design.
  getProcurementBuyers: async (callerMobile: string, activeOnly = false): Promise<any> => {
    try {
      const response = await api.get<any>(
        `${API_ENDPOINTS.farmManagementProcurementBuyers()}${activeOnly ? '?active_only=true' : ''}`,
        { headers: { 'x-caller-mobile': callerMobile } },
      );
      return response.data;
    } catch (error: any) {
      console.error('Error fetching procurement buyers:', error);
      return { error: extractErrorMessage(error) };
    }
  },

  // Returns `created: false` (still 200) rather than an error when the phone is
  // already listed — buyers are de-duplicated on the phone, not the name.
  // `phone` is optional here: the Add Buyer form no longer collects one, so
  // callers can omit it entirely rather than send an empty string.
  addProcurementBuyer: async (callerMobile: string, data: { name: string; phone?: string }): Promise<any> => {
    try {
      const response = await api.post<any>(API_ENDPOINTS.farmManagementProcurementBuyers(), data, {
        headers: { 'x-caller-mobile': callerMobile },
      });
      return response.data;
    } catch (error: any) {
      console.error('Error adding procurement buyer:', error);
      return { error: extractErrorMessage(error) };
    }
  },

  // Retire or restore, both directions. A soft delete either way — the record
  // always survives, so a buyer who returns is restored rather than re-entered
  // (which the phone key would refuse anyway).
  setProcurementBuyerStatus: async (callerMobile: string, buyerId: string, isActive: boolean): Promise<any> => {
    try {
      const response = await api.patch<any>(
        API_ENDPOINTS.farmManagementBuyerStatus(buyerId),
        { isActive },
        { headers: { 'x-caller-mobile': callerMobile } },
      );
      return response.data;
    } catch (error: any) {
      console.error('Error updating buyer status:', error);
      return { error: extractErrorMessage(error) };
    }
  },

  // Same toggle for markets. Note the gate differs from the buyer one — Farm
  // Manager / SuperAdmin here, so a buyer-manager is 403'd.
  setProcurementMarketStatus: async (callerMobile: string, marketId: string, isActive: boolean): Promise<any> => {
    try {
      const response = await api.patch<any>(
        API_ENDPOINTS.farmManagementMarketStatus(marketId),
        { isActive },
        { headers: { 'x-caller-mobile': callerMobile } },
      );
      return response.data;
    } catch (error: any) {
      console.error('Error updating market status:', error);
      return { error: extractErrorMessage(error) };
    }
  },

  getProcurementBuffaloes: async (callerMobile: string, params: { stage: string; page: number; page_size: number; search?: string; from_date?: string; to_date?: string }): Promise<any> => {
    try {
      const response = await api.get<any>(API_ENDPOINTS.farmManagementProcurementBuffaloes(), {
        params,
        headers: { 'x-caller-mobile': callerMobile },
      });
      return response.data;
    } catch (error: any) {
      console.error('Error fetching procurement buffaloes:', error);
      return { error: extractErrorMessage(error) };
    }
  },

  getProcurementBuffaloDetail: async (callerMobile: string, buffaloId: string): Promise<any> => {
    try {
      const response = await api.get<any>(API_ENDPOINTS.farmManagementProcurementBuffaloDetail(buffaloId), {
        headers: { 'x-caller-mobile': callerMobile },
      });
      return response.data;
    } catch (error: any) {
      console.error('Error fetching procurement buffalo detail:', error);
      return { error: extractErrorMessage(error) };
    }
  },

  // Same endpoint serves two independent partial updates: buffalo/calf detail fields,
  // or the driver+batch trio (batchId included so the caller can move the buffalo to a
  // different batch). Caller passes only the fields relevant to the edit being made.
  updateProcurementBuffalo: async (callerMobile: string, buffaloId: string, fields: Record<string, any>): Promise<any> => {
    try {
      const response = await api.patch<any>(API_ENDPOINTS.farmManagementProcurementBuffaloDetail(buffaloId), fields, {
        headers: { 'x-caller-mobile': callerMobile },
      });
      return response.data;
    } catch (error: any) {
      console.error('Error updating procurement buffalo:', error);
      return { error: extractErrorMessage(error) };
    }
  },

  advanceProcurementBuffaloes: async (callerMobile: string, payload: { buffaloIds: string[]; stage: string; updated_by_role?: string; updated_by_name?: string; updated_by_number?: string; driverName?: string; driverNumber?: string; vehicleNo?: string; photoUrls?: string[]; comments?: string }): Promise<any> => {
    try {
      const response = await api.post<any>(API_ENDPOINTS.farmManagementAdvanceBuffaloes(), payload, {
        headers: { 'x-caller-mobile': callerMobile },
      });
      return response.data;
    } catch (error: any) {
      console.error('Error advancing buffaloes:', error);
      return { error: extractErrorMessage(error) };
    }
  },

  editProcurementHalt: async (callerMobile: string, haltId: string, payload: { driverName?: string; driverNumber?: string; vehicleNo?: string; photoUrls?: string[] }): Promise<any> => {
    try {
      const response = await api.patch<any>(API_ENDPOINTS.farmManagementEditHalt(haltId), payload, {
        headers: { 'x-caller-mobile': callerMobile },
      });
      return response.data;
    } catch (error: any) {
      console.error('Error editing halt:', error);
      return { error: extractErrorMessage(error) };
    }
  },

  getProcurementDeaths: async (callerMobile: string, params: { page: number; page_size: number; search?: string; animal?: 'all' | 'buffalo' | 'calf' }): Promise<any> => {
    try {
      const response = await api.get<any>(API_ENDPOINTS.farmManagementProcurementDeaths(), {
        params,
        headers: { 'x-caller-mobile': callerMobile },
      });
      return response.data;
    } catch (error: any) {
      console.error('Error fetching procurement deaths:', error);
      return { error: extractErrorMessage(error) };
    }
  },

  getProcurementOrphanCalves: async (callerMobile: string, params: { page: number; page_size: number; search?: string }): Promise<any> => {
    try {
      const response = await api.get<any>(API_ENDPOINTS.farmManagementProcurementOrphanCalves(), {
        params,
        headers: { 'x-caller-mobile': callerMobile },
      });
      return response.data;
    } catch (error: any) {
      console.error('Error fetching procurement orphan calves:', error);
      return { error: extractErrorMessage(error) };
    }
  },

  markBuffaloDead: async (callerMobile: string, buffaloId: string, payload: { reason: string; photoUrls?: string[]; calf?: 'orphan' | 'died'; calfReason?: string; calfPhotoUrls?: string[] }): Promise<any> => {
    try {
      const response = await api.post<any>(API_ENDPOINTS.farmManagementProcurementBuffaloDeath(buffaloId), payload, {
        headers: { 'x-caller-mobile': callerMobile },
      });
      return response.data;
    } catch (error: any) {
      console.error('Error marking buffalo dead:', error);
      return { error: extractErrorMessage(error) };
    }
  },

  markCalfDead: async (callerMobile: string, calfId: string, payload: { reason: string; photoUrls?: string[] }): Promise<any> => {
    try {
      const response = await api.post<any>(API_ENDPOINTS.farmManagementProcurementCalfDeath(calfId), payload, {
        headers: { 'x-caller-mobile': callerMobile },
      });
      return response.data;
    } catch (error: any) {
      console.error('Error marking calf dead:', error);
      return { error: extractErrorMessage(error) };
    }
  },

  getSummary: async (callerMobile: string): Promise<any> => {
    try {
      const response = await api.get<any>(API_ENDPOINTS.farmManagementSummary(), {
        headers: { 'x-caller-mobile': callerMobile },
      });
      return response.data;
    } catch (error: any) {
      console.error('Error fetching procurement summary:', error);
      return { error: extractErrorMessage(error) };
    }
  },

  getTodayTarget: async (callerMobile: string): Promise<any> => {
    try {
      const response = await api.get<any>(API_ENDPOINTS.farmManagementTodayTarget(), {
        headers: { 'x-caller-mobile': callerMobile },
      });
      return response.data;
    } catch (error: any) {
      console.error('Error fetching today target:', error);
      return { error: extractErrorMessage(error) };
    }
  },

  getMyAssistants: async (callerMobile: string): Promise<any> => {
    try {
      const response = await api.get<any>(API_ENDPOINTS.farmManagementMyAssistants(), {
        headers: { 'x-caller-mobile': callerMobile },
      });
      return response.data;
    } catch (error: any) {
      console.error('Error fetching my assistants:', error);
      return { error: extractErrorMessage(error) };
    }
  },

  assign: async (callerMobile: string, assistantMobile: string, buffaloIds: string[]): Promise<any> => {
    try {
      const response = await api.post(API_ENDPOINTS.farmManagementAssign(),
        { assistantMobile, buffaloIds },
        { headers: { 'x-caller-mobile': callerMobile, 'Content-Type': 'application/json' } });
      return response.data;
    } catch (error: any) {
      console.error('Error assigning buffaloes:', error);
      return { error: extractErrorMessage(error) };
    }
  },

  selfAssign: async (callerMobile: string, buffaloIds: string[]): Promise<any> => {
    try {
      const response = await api.post(API_ENDPOINTS.farmManagementSelfAssign(),
        { buffaloIds },
        { headers: { 'x-caller-mobile': callerMobile, 'Content-Type': 'application/json' } });
      return response.data;
    } catch (error: any) {
      console.error('Error self-assigning buffaloes:', error);
      return { error: extractErrorMessage(error) };
    }
  },

  unassign: async (callerMobile: string, buffaloIds: string[]): Promise<any> => {
    try {
      const response = await api.post(API_ENDPOINTS.farmManagementUnassign(),
        { buffaloIds },
        { headers: { 'x-caller-mobile': callerMobile, 'Content-Type': 'application/json' } });
      return response.data;
    } catch (error: any) {
      console.error('Error unassigning buffaloes:', error);
      return { error: extractErrorMessage(error) };
    }
  },

  getBuffalo: async (callerMobile: string, buffaloId: string): Promise<any> => {
    try {
      const response = await api.get<any>(API_ENDPOINTS.farmManagementBuffalo(buffaloId), {
        headers: { 'x-caller-mobile': callerMobile },
      });
      return response.data;
    } catch (error: any) {
      console.error('Error fetching buffalo:', error);
      return { error: extractErrorMessage(error) };
    }
  },

  updateBuffalo: async (callerMobile: string, buffaloId: string, fields: Record<string, any>): Promise<any> => {
    try {
      const response = await api.put(API_ENDPOINTS.farmManagementBuffalo(buffaloId), fields, {
        headers: { 'x-caller-mobile': callerMobile, 'Content-Type': 'application/json' },
      });
      return response.data;
    } catch (error: any) {
      console.error('Error updating buffalo:', error);
      return { error: extractErrorMessage(error) };
    }
  },

  setBuffaloProcured: async (callerMobile: string, buffaloId: string, procured: boolean): Promise<any> => {
    try {
      const response = await api.put(API_ENDPOINTS.farmManagementBuffaloProcured(buffaloId), { procured }, {
        headers: { 'x-caller-mobile': callerMobile, 'Content-Type': 'application/json' },
      });
      return response.data;
    } catch (error: any) {
      console.error('Error updating procured status:', error);
      return { error: extractErrorMessage(error) };
    }
  },

  updateCalf: async (callerMobile: string, calfId: string, fields: Record<string, any>): Promise<any> => {
    try {
      const response = await api.put(API_ENDPOINTS.farmManagementCalf(calfId), fields, {
        headers: { 'x-caller-mobile': callerMobile, 'Content-Type': 'application/json' },
      });
      return response.data;
    } catch (error: any) {
      console.error('Error updating calf:', error);
      return { error: extractErrorMessage(error) };
    }
  },

  attachBuffaloPhotos: async (callerMobile: string, buffaloId: string, urls: string[], kind?: string): Promise<any> => {
    try {
      const response = await api.post(API_ENDPOINTS.farmManagementBuffaloPhotos(buffaloId), { urls, kind }, {
        headers: { 'x-caller-mobile': callerMobile, 'Content-Type': 'application/json' },
      });
      return response.data;
    } catch (error: any) {
      console.error('Error attaching buffalo photos:', error);
      return { error: extractErrorMessage(error) };
    }
  },

  attachCalfPhotos: async (callerMobile: string, calfId: string, urls: string[], kind?: string): Promise<any> => {
    try {
      const response = await api.post(API_ENDPOINTS.farmManagementCalfPhotos(calfId), { urls, kind }, {
        headers: { 'x-caller-mobile': callerMobile, 'Content-Type': 'application/json' },
      });
      return response.data;
    } catch (error: any) {
      console.error('Error attaching calf photos:', error);
      return { error: extractErrorMessage(error) };
    }
  },
};

export const farmService = {
  getFarms: async (status?: string): Promise<Farm[]> => {
    try {
      const params = status && status !== '--' ? { status } : {};
      const response = await api.get<any>(API_ENDPOINTS.getFarms(), { params });
      
      // Handle both direct array and wrapped object responses ({ farms: [...] })
      if (Array.isArray(response.data)) {
        return response.data;
      } else if (response.data && Array.isArray(response.data.farms)) {
        return response.data.farms;
      }
      
      return [];
    } catch (error) {
      console.error('Error fetching farms:', error);
      throw error;
    }
  },

  addFarm: async (farmData: CreateFarmRequest, adminMobile: string, adminOtp: string): Promise<ApiResponse<any>> => {
    try {
      const response = await api.post(API_ENDPOINTS.addFarm(), farmData, {
        headers: {
          'X-Admin-Mobile': adminMobile,
          'x-admin-otp': adminOtp,
          'Content-Type': 'application/json'
        }
      });

      if (response.data && (response.data.status === 'error' || response.data.statuscode >= 400)) {
        return { error: response.data.message || 'Failed to add farm' };
      }

      return { data: response.data, message: 'Farm added successfully' };
    } catch (error: any) {
      console.error('Error adding farm:', error);
      return { error: extractErrorMessage(error) };
    }
  },

  updateFarm: async (farmId: string, farmData: CreateFarmRequest, adminMobile: string, adminOtp: string): Promise<ApiResponse<any>> => {
    try {
      const response = await api.put(API_ENDPOINTS.updateFarm(farmId), farmData, {
        headers: {
          'X-Admin-Mobile': adminMobile,
          'x-admin-otp': adminOtp,
          'Content-Type': 'application/json'
        }
      });

      if (response.data && (response.data.status === 'error' || response.data.statuscode >= 400)) {
        return { error: response.data.message || 'Failed to update farm' };
      }

      return { data: response.data, message: 'Farm updated successfully' };
    } catch (error: any) {
      console.error('Error updating farm:', error);
      return { error: extractErrorMessage(error) };
    }
  },

  assignAdminToFarm: async (farmId: string, adminMobile: string, superAdminMobile: string, superAdminOtp: string): Promise<ApiResponse<any>> => {
    try {
      const payload: AssignAdminToFarmRequest = { admin_mobile: adminMobile };
      const response = await api.post(API_ENDPOINTS.assignAdminToFarm(farmId), payload, {
        headers: {
          'x-admin-mobile': superAdminMobile,
          'x-admin-otp': superAdminOtp,
          'Content-Type': 'application/json'
        }
      });

      if (response.data && (response.data.status === 'error' || response.data.statuscode >= 400)) {
        return { error: response.data.message || 'Failed to assign admin' };
      }

      return { data: response.data, message: 'Admin assigned successfully' };
    } catch (error: any) {
      console.error('Error assigning admin:', error);
      return { error: extractErrorMessage(error) };
    }
  }
};

export const otpService = {
  sendOtp: async (mobile: string, appName: string = 'animalkart_dashboard'): Promise<ApiResponse<any>> => {
    try {
      const response = await api.post(API_ENDPOINTS.sendOtp(), { mobile, appName });
      return { data: response.data, message: 'OTP sent successfully' };
    } catch (error: any) {
      console.error('Error sending OTP:', error);
      return { error: extractErrorMessage(error) };
    }
  },

  // Invalidates every access token issued to this mobile so far (server-side —
  // a stateless JWT can't be deleted, but the backend rejects anything issued
  // before this call from now on). Call before clearing the local token so a
  // stale copy left in browser storage/cache can't still be used elsewhere.
  logout: async (mobile: string): Promise<void> => {
    try {
      await api.post(API_ENDPOINTS.logout(), { mobile });
    } catch (error) {
      // Best-effort — the local token is cleared regardless (see App.tsx
      // handleLogout), so a failed server-side call shouldn't block logout.
      console.error('Error logging out:', error);
    }
  },
};

export const sessionApi = {
  /** Recent logins for one user — newest first, capped at 10 by the backend. */
  getUserSessions: async (mobile: string): Promise<UserSession[]> => {
    const res = await api.get(API_ENDPOINTS.userSessions(mobile));
    return res?.data?.sessions ?? [];
  },
};

export const ocrService = {
  extractText: async (file: File): Promise<{ text: string; cardType?: string } | null> => {
    try {
      const formData = new FormData();
      formData.append('file', file);
      const response = await api.post<any>(API_ENDPOINTS.ocrExtractBatch(), formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      const result = response.data?.results?.[0];
      if (result?.status === 'success' && result?.ocr_text) {
        return { text: result.ocr_text, cardType: result.card_type };
      }
      return null;
    } catch (error) {
      console.error('Error extracting OCR text:', error);
      return null;
    }
  },
};

export const selfBenefitService = {
  getSelfBenefits: async (farmId?: string): Promise<SelfBenefit[]> => {
    try {
      const params: Record<string, any> = { include_disabled: true };
      if (farmId) params.farm_id = farmId;
      const response = await api.get<SelfBenefit[]>(API_ENDPOINTS.getSelfBenefits(), { params });
      return response.data || [];
    } catch (error) {
      console.error('Error fetching self benefits:', error);
      throw error;
    }
  },

  createSelfBenefit: async (benefitData: CreateSelfBenefitRequest, adminMobile: string, adminOtp: string): Promise<ApiResponse<any>> => {
    try {
      const response = await api.post(API_ENDPOINTS.getSelfBenefits(), benefitData, {
        headers: {
          'x-admin-mobile': adminMobile,
          'x-admin-otp': adminOtp
        }
      });

      if (response.data && (response.data.status === 'error' || response.data.statuscode >= 400)) {
        return { error: response.data.message || 'Failed to create benefit' };
      }

      return { data: response.data, message: 'Benefit created successfully' };
    } catch (error: any) {
      console.error('Error creating self benefit:', error);
      return { error: extractErrorMessage(error) };
    }
  },

  updateSelfBenefit: async (benefitId: string, benefitData: CreateSelfBenefitRequest, adminMobile: string, adminOtp: string): Promise<ApiResponse<any>> => {
    try {
      // payload no longer requires id in the body
      const { id, ...payload } = benefitData as any;
      const response = await api.put(API_ENDPOINTS.updateSelfBenefit(encodeURIComponent(benefitId)), payload, {
        headers: {
          'x-admin-mobile': adminMobile,
          'x-admin-otp': adminOtp,
          'id': benefitId
        }
      });

      if (response.data && (response.data.status === 'error' || response.data.statuscode >= 400)) {
        return { error: response.data.message || 'Failed to update benefit' };
      }

      return { data: response.data, message: 'Benefit updated successfully' };
    } catch (error: any) {
      console.error('Error updating self benefit:', error);
      return { error: extractErrorMessage(error) };
    }
  },


};

export const couponService = {
  generateCoupons: async (data: GenerateCouponsRequest, adminMobile: string): Promise<ApiResponse<any>> => {
    try {
      const response = await api.post(API_ENDPOINTS.generateCoupons(), data, {
        headers: { 'x-admin-mobile': adminMobile },
      });
      if (response.data && (response.data.status === 'error' || response.data.statuscode >= 400)) {
        return { error: response.data.message || 'Failed to generate coupons' };
      }
      return { data: response.data, message: 'Coupons generated' };
    } catch (error: any) {
      return { error: extractErrorMessage(error) };
    }
  },

  getCoupons: async (adminMobile: string, statusFilter?: string, search?: string): Promise<{ coupons: Coupon[]; tiers: any[]; total: number }> => {
    try {
      const params: Record<string, string> = {};
      if (statusFilter) params.status = statusFilter;
      if (search) params.search = search;
      const response = await api.get<any>(API_ENDPOINTS.getCoupons(), {
        headers: { 'x-admin-mobile': adminMobile },
        params,
      });
      const data = response.data || {};
      return {
        coupons: data.coupons || [],
        tiers: data.tiers || [],
        total: data.total || 0,
      };
    } catch (error) {
      console.error('Error fetching coupons:', error);
      throw error;
    }
  },

  deleteCoupon: async (code: string, adminMobile: string): Promise<ApiResponse<any>> => {
    try {
      const response = await api.delete(API_ENDPOINTS.deleteCoupon(code), {
        headers: { 'x-admin-mobile': adminMobile },
      });
      if (response.data && (response.data.status === 'error' || response.data.statuscode >= 400)) {
        return { error: response.data.message || 'Failed to delete coupon' };
      }
      return { data: response.data, message: 'Coupon deleted' };
    } catch (error: any) {
      return { error: extractErrorMessage(error) };
    }
  },

  assignCoupon: async (data: AssignCouponRequest, adminMobile: string): Promise<ApiResponse<any>> => {
    try {
      const response = await api.post(API_ENDPOINTS.assignCoupon(), data, {
        headers: { 'x-admin-mobile': adminMobile },
      });
      if (response.data && (response.data.status === 'error' || response.data.statuscode >= 400)) {
        return { error: response.data.message || 'Failed to assign coupon' };
      }
      return { data: response.data, message: 'Coupon assigned' };
    } catch (error: any) {
      return { error: extractErrorMessage(error) };
    }
  },

  applyCoupon: async (data: ApplyCouponRequest, adminMobile: string): Promise<ApiResponse<any>> => {
    try {
      const response = await api.post(API_ENDPOINTS.applyCoupon(), data, {
        headers: { 'x-admin-mobile': adminMobile },
      });
      if (response.data && (response.data.status === 'error' || response.data.statuscode >= 400)) {
        return { error: response.data.message || 'Failed to apply coupon' };
      }
      return { data: response.data, message: 'Coupon applied' };
    } catch (error: any) {
      return { error: extractErrorMessage(error) };
    }
  },

  removeCoupon: async (data: ApplyCouponRequest, adminMobile: string): Promise<ApiResponse<any>> => {
    try {
      const response = await api.post(API_ENDPOINTS.removeCoupon(), data, {
        headers: { 'x-admin-mobile': adminMobile },
      });
      if (response.data && (response.data.status === 'error' || response.data.statuscode >= 400)) {
        return { error: response.data.message || 'Failed to remove coupon' };
      }
      return { data: response.data, message: 'Coupon removed' };
    } catch (error: any) {
      return { error: extractErrorMessage(error) };
    }
  },

  getOrderCoupon: async (orderId: string, adminMobile: string): Promise<OrderCouponBlock | null> => {
    try {
      const response = await api.get<any>(API_ENDPOINTS.getOrderCoupon(orderId), {
        headers: { 'x-admin-mobile': adminMobile },
      });
      return (response.data && response.data.order) || null;
    } catch (error) {
      console.error('Error fetching order coupon:', error);
      return null;
    }
  },

  getUserCoupons: async (mobile: string, status?: string): Promise<Coupon[]> => {
    try {
      const response = await api.get<any>(API_ENDPOINTS.getUserCoupons(mobile), {
        params: status ? { status } : undefined,
      });
      return response.data?.coupons || [];
    } catch (error) {
      console.error('Error fetching user coupons:', error);
      return [];
    }
  },

  getCouponHistory: async (
    adminMobile: string,
    opts?: { action?: string; code?: string; search?: string; skip?: number; limit?: number },
  ): Promise<{ events: any[]; counts: Record<string, number>; total: number }> => {
    try {
      const response = await api.get<any>(API_ENDPOINTS.couponHistory(), {
        headers: { 'x-admin-mobile': adminMobile },
        params: opts,
      });
      return {
        events: response.data?.events || [],
        counts: response.data?.counts || {},
        total: response.data?.total || 0,
      };
    } catch (error) {
      console.error('Error fetching coupon history:', error);
      return { events: [], counts: {}, total: 0 };
    }
  },
};

export const referralBenefitService = {
  getReferralMilestones: async (farmId?: string): Promise<ReferralMilestone[]> => {
    try {
      const params: Record<string, any> = { include_disabled: true };
      if (farmId) params.farm_id = farmId;
      const response = await api.get<ReferralMilestone[]>(API_ENDPOINTS.getReferralMilestones(), { params });
      return response.data || [];
    } catch (error) {
      console.error('Error fetching referral milestones:', error);
      throw error;
    }
  },
  createReferralMilestone: async (data: CreateReferralMilestoneRequest, adminMobile: string, adminOtp: string): Promise<ApiResponse<ReferralMilestone>> => {
    try {
      const response = await api.post<ApiResponse<ReferralMilestone>>(API_ENDPOINTS.getReferralMilestones(), data, {
        headers: {
          'x-admin-mobile': adminMobile,
          'x-admin-otp': adminOtp
        }
      });
      return response.data;
    } catch (error) {
      console.error('Error creating referral milestone:', error);
      throw error;
    }
  },
  updateReferralMilestone: async (id: string, data: CreateReferralMilestoneRequest, adminMobile: string, adminOtp: string): Promise<ApiResponse<ReferralMilestone>> => {
    try {
      const response = await api.put<ApiResponse<ReferralMilestone>>(API_ENDPOINTS.updateReferralMilestone(id), data, {
        headers: {
          'x-admin-mobile': adminMobile,
          'x-admin-otp': adminOtp
        }
      });
      return response.data;
    } catch (error) {
      console.error('Error updating referral milestone:', error);
      throw error;
    }
  },
  getAchievedReferralMilestones: async (adminMobile: string): Promise<any> => {
    try {
      const response = await api.get(API_ENDPOINTS.getAchievedReferralMilestones!(), {
        headers: {
          'x-admin-mobile': adminMobile
        }
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching achieved referral milestones:', error);
      throw error;
    }
  }
};

export const referralConfigService = {
  getReferralConfig: async (farmId?: string): Promise<ReferralConfig> => {
    try {
      const params = farmId ? { farm_id: farmId } : {};
      const response = await api.get<ReferralConfig>(API_ENDPOINTS.getReferralConfig(), { params });
      return response.data;
    } catch (error) {
      console.error('Error fetching referral config:', error);
      throw error;
    }
  },
  updateReferralConfig: async (data: UpdateReferralConfigRequest, adminMobile: string, adminOtp: string): Promise<ApiResponse<ReferralConfig>> => {
    try {
      const response = await api.put<ApiResponse<ReferralConfig>>(API_ENDPOINTS.updateReferralConfig(), data, {
        headers: {
          'x-admin-mobile': adminMobile,
          'x-admin-otp': adminOtp
        }
      });
      return response.data;
    } catch (error) {
      console.error('Error updating referral config:', error);
      throw error;
    }
  }
};

export const roleRequestService = {
  getRoleChangeRequests: async (statusFilter: string = 'ALL', adminMobile: string): Promise<RoleChangeRequest[]> => {
    try {
      const params = {
        status_filter: statusFilter,
        admin_mobile: adminMobile
      };
      const response = await api.get<RoleChangeRequestResponse>(API_ENDPOINTS.getRoleChangeRequests(), { params });
      return response.data.data || [];
    } catch (error) {
      console.error('Error fetching role change requests:', error);
      throw error;
    }
  },
  actionRoleChangeRequest: async (requestId: string, approved: boolean, adminMobile: string): Promise<ApiResponse<any>> => {
    try {
      const response = await api.put(API_ENDPOINTS.actionRoleChangeRequest(requestId), { approved }, {
        params: { admin_mobile: adminMobile },
        headers: {
          'X-Admin-Mobile': adminMobile,
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        }
      });
      return { data: response.data, message: `Request ${approved ? 'approved' : 'rejected'} successfully` };
    } catch (error: any) {
      console.error(`Error ${approved ? 'approving' : 'rejecting'} role request:`, error);
      const errorMessage = error?.response?.data?.message || error?.response?.data?.detail || `Failed to ${approved ? 'approve' : 'reject'} request`;
      return { error: errorMessage };
    }
  }
};

export const achievedBenefitService = {
  getAchievedBenefits: async (adminMobile: string): Promise<any> => {
    try {
      const response = await api.get(API_ENDPOINTS.getAchievedBenefits(), {
        headers: {
          'x-admin-mobile': adminMobile
        }
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching achieved benefits:', error);
      throw error;
    }
  }
};

export const orderService = {
  getOrderDetails: async (orderId: string): Promise<any> => {

    try {
      const response = await api.get<any>(API_ENDPOINTS.getOrderDetails(orderId));
      return response.data;
    } catch (error) {
      console.error('Error fetching order details:', error);
      throw error;
    }
  },

  validateOrderSession: async (token: string, orderId: string): Promise<{
    statuscode: number;
    valid: boolean;
    reason?: string;
    orderId?: string;
    role?: string;
    expiredAt?: string;
  }> => {
    try {
      const response = await api.get<any>(API_ENDPOINTS.validateOrderSession(token, orderId));
      return response.data;
    } catch (error) {
      console.error('Error validating order session:', error);
      return { statuscode: 500, valid: false, reason: 'Failed to validate session' };
    }
  },
};

export const procurementService = {
  // What a Delete & Refund on this order would move, before any OTP is spent.
  // Read-only on the backend, so the modal may call it on every open.
  getRefundPreview: async (orderId: string, adminMobile: string): Promise<RefundPreview> => {
    const response = await api.get<any>(API_ENDPOINTS.refundPreview(orderId), {
      headers: { 'x-admin-mobile': adminMobile },
    });
    const data = response.data;
    if (!data || data.statuscode !== 200) {
      throw new Error(data?.message || 'Could not load the refund breakdown');
    }
    return data as RefundPreview;
  },

  // Global roster search: matching order groups per stage (routers/roster_search.py).
  searchProcurementRoster: async (search: string, onboardedByMobile?: string): Promise<{ stages: { stage: string; orders: number }[]; first_stage: string | null; total: number }> => {
    const response = await api.get<any>(API_ENDPOINTS.searchProcurementRoster(), {
      params: { search, ...(onboardedByMobile ? { onboardedByMobile } : {}) },
    });
    if (response.data?.status === 'error') throw new Error(response.data.message || 'Search failed');
    return { stages: response.data?.stages || [], first_stage: response.data?.first_stage ?? null, total: response.data?.total ?? 0 };
  },

  getProcurementRoster: async (params: { stage: string; date?: string; page?: number; page_size?: number; search?: string; mobile?: string; cpf_filter?: string; onboardedByMobile?: string }): Promise<{ data: any[], totalGroups?: number, stageCounts?: { procurement: number, marketQuarantine: number, inTransit: number, localFarmQuarantine: number, readyToDeliver: number, delivered: number } }> => {
    try {
      const response = await api.get<any>(API_ENDPOINTS.getProcurementRoster(), { params });
      const totalGroups = response.data?.totalGroups ?? response.data?.total_groups;

      const stageCounts = {
        procurement: response.data?.procurement_buffalo_count ?? 0,
        marketQuarantine: response.data?.market_quarantine_buffalo_count ?? 0,
        inTransit: response.data?.intransit_buffalo_count ?? 0,
        localFarmQuarantine: response.data?.localFarmQuarantine_buffalo_count ?? 0,
        readyToDeliver: response.data?.readyToDeliver_buffalo_count ?? 0,
        delivered: response.data?.delivered_buffalo_count ?? 0,
      };

      // Case 1: Grouped data (Basket stage)
      if (response.data && Array.isArray(response.data.data)) {
        return { data: response.data.data, totalGroups: totalGroups ?? response.data.data.length, stageCounts };
      }

      // Case 2: Flat list (Procurement/Tracking stages)
      if (response.data && Array.isArray(response.data.buffaloes)) {
        const flatData = [{
          approvedDate: response.data.filterDate || null,
          totalBuffaloes: response.data.totalBuffaloes || response.data.buffaloes.length,
          buffaloes: response.data.buffaloes
        }];
        return { data: flatData, totalGroups: totalGroups ?? 1, stageCounts };
      }

      return { data: [], totalGroups: 0, stageCounts };
    } catch (error) {
      console.error('Error fetching procurement roster:', error);
      throw error;
    }
  },

  performProcurementAction: async (data: {
    action: string;
    buffaloIds: string[];
    marketName?: string;
    marketId?: string;
    sendNotification: boolean;
    adminMobile: string;
  }): Promise<any> => {
    try {
      const { adminMobile, ...payload } = data;
      const response = await api.post<any>(API_ENDPOINTS.procurementAction(), payload, {
        headers: {
          'x-admin-mobile': adminMobile || 'true'
        }
      });
      return response.data;
    } catch (error) {
      console.error('Error performing procurement action:', error);
      throw error;
    }
  }
};

export const marketService = {
  getMarkets: async (): Promise<Market[]> => {
    try {
      const response = await api.get<any>(API_ENDPOINTS.getMarkets());
      // Handle both direct array and wrapped object responses
      if (Array.isArray(response.data)) {
        return response.data;
      } else if (response.data && Array.isArray(response.data.markets)) {
        return response.data.markets;
      }
      return [];
    } catch (error) {
      console.error('Error fetching markets:', error);
      throw error;
    }
  },

  addMarket: async (marketData: CreateMarketRequest, adminMobile: string): Promise<ApiResponse<any>> => {
    try {
      const response = await api.post(API_ENDPOINTS.addMarket(), marketData, {
        headers: {
          'x-admin-mobile': adminMobile,
          'Content-Type': 'application/json'
        }
      });

      if (response.data && (response.data.status === 'error' || response.data.statuscode >= 400)) {
        return { error: response.data.message || 'Failed to add market' };
      }

      return { data: response.data, message: 'Market added successfully' };
    } catch (error: any) {
      console.error('Error adding market:', error);
      return { error: extractErrorMessage(error) };
    }
  },

  updateMarket: async (marketId: string, marketData: CreateMarketRequest, adminMobile: string): Promise<ApiResponse<any>> => {
    try {
      const response = await api.put(API_ENDPOINTS.updateMarket(marketId), marketData, {
        headers: {
          'x-admin-mobile': adminMobile,
          'Content-Type': 'application/json'
        }
      });

      if (response.data && (response.data.status === 'error' || response.data.statuscode >= 400)) {
        return { error: response.data.message || 'Failed to update market' };
      }

      return { data: response.data, message: 'Market updated successfully' };
    } catch (error: any) {
      console.error('Error updating market:', error);
      return { error: extractErrorMessage(error) };
    }
  },

  toggleMarketStatus: async (marketId: string, isActive: boolean, adminMobile: string): Promise<ApiResponse<any>> => {
    try {
      const response = await api.patch(API_ENDPOINTS.toggleMarketStatus(marketId), { isActive }, {
        headers: {
          'x-admin-mobile': adminMobile,
          'Content-Type': 'application/json'
        }
      });

      if (response.data && (response.data.status === 'error' || response.data.statuscode >= 400)) {
        return { error: response.data.message || 'Failed to toggle status' };
      }

      return { data: response.data, message: `Market ${isActive ? 'activated' : 'deactivated'} successfully` };
    } catch (error: any) {
      console.error('Error toggling market status:', error);
      return { error: extractErrorMessage(error) };
    }
  }
};

export const adminOrderService = {
  createOrder: async (orderData: AdminCreateOrderRequest, adminMobile: string): Promise<ApiResponse<any>> => {
    try {
      // All order creation goes through buy-with-payment (create order + first
      // transaction(s) atomically). Every order is created with at least one transaction;
      // a full-coin order sends a 0-amount stub the coin path ignores. A customer who
      // overpaid gets a second, separate SURPLUS-method transaction in the same array.
      //
      // The caller identifies the actor via paymentUpdatedBy* (current modals) or the
      // legacy orderCreatedBy* fields. In this single create+pay call the same actor is
      // both the order creator AND the payment updater, so we feed both from whichever
      // set is populated — otherwise the employee's identity is lost and the order is
      // neither held (PENDING_SUBMISSION) nor attributed correctly.
      const actorMobile = orderData.paymentUpdatedByMobile ?? orderData.orderCreatedByMobile;
      const actorName = orderData.paymentUpdatedByName ?? orderData.orderCreatedByName;
      const actorRole = orderData.paymentUpdatedByRole ?? orderData.orderCreatedByRole;
      const actorDate = orderData.orderCreatedDate ?? (orderData as any).paymentUpdatedDate;

      const bwpPayload: any = {
        userId: orderData.userId,
        breedId: orderData.breedId,
        numUnits: orderData.numUnits,
        withCpf: orderData.withCpf ?? false,
        cpfUnitCost: orderData.cpfUnitCost ?? 0,
        baseUnitCost: orderData.baseUnitCost,
        coinsRedeemed: orderData.coinsRedeemed ?? 0,
        farmId: orderData.farmId,
        paymentMode: orderData.paymentMode,
        paymentType: orderData.paymentType,
        transactions: orderData.transactions ?? [{ amount: 0 }],
        paymentUpdatedByMobile: actorMobile,
        paymentUpdatedByName: actorName,
        paymentUpdatedByRole: actorRole,
        paymentUpdatedDate: actorDate,
        orderCreatedByMobile: actorMobile,
        orderCreatedByName: actorName,
        orderCreatedByRole: actorRole,
        orderCreatedDate: actorDate,
        order_onboarded_by_mobile: orderData.orderOnboardedByMobile || undefined,
        comment: orderData.comment || undefined,
        source: 'animalkart_dashboard',
      };

      const response = await api.post(API_ENDPOINTS.buyWithPayment(), bwpPayload, {
        headers: { 'x-admin-mobile': adminMobile, 'Content-Type': 'application/json' },
      });

      if (response.data && (response.data.status === 'error' || response.data.statuscode >= 400)) {
        return { error: response.data.message || 'Failed to create order' };
      }

      return { data: response.data, message: 'Order created successfully' };
    } catch (error: any) {
      console.error('Error creating admin order:', error);
      return { error: extractErrorMessage(error) };
    }
  },

  getUserOrders: async (mobile: string): Promise<any[]> => {
    try {
      const response = await api.get<any>(API_ENDPOINTS.getUserOrders(mobile));
      return response.data?.orders || [];
    } catch (error) {
      console.error('Error fetching user orders:', error);
      return [];
    }
  }
};

export const leadService = {
  upload: async (callerMobile: string, rows: any[]): Promise<any> => {
    const res = await api.post(API_ENDPOINTS.leadsUpload(), { rows },
      { headers: { 'x-caller-mobile': callerMobile } });
    return res.data;
  },
  list: async (
    callerMobile: string,
    params?: { uploaded_date?: string; added_by_id?: string; lead_status?: string;
               assigned_to_mobile?: string; only_assigned?: boolean; only_unassigned?: boolean; search?: string;
               from_date?: string; to_date?: string },
  ): Promise<any> => {
    const res = await api.get(API_ENDPOINTS.leadsBase(),
      { params, headers: { 'x-caller-mobile': callerMobile } });
    return res.data;
  },
  // One lead's full record, for the Lead Details page — same fields the list
  // already carries per row (status_history/assignment_history included),
  // fetched fresh for a direct page load rather than relying on an
  // already-fetched list row being in memory.
  get: async (callerMobile: string, leadId: string): Promise<any> => {
    const res = await api.get(API_ENDPOINTS.leadsDetail(leadId),
      { headers: { 'x-caller-mobile': callerMobile } });
    return res.data;
  },
  assign: async (callerMobile: string, leadIds: string[], assigneeMobile: string): Promise<any> => {
    const res = await api.post(API_ENDPOINTS.leadsAssign(),
      { lead_ids: leadIds, assignee_mobile: assigneeMobile },
      { headers: { 'x-caller-mobile': callerMobile } });
    return res.data;
  },
  updateStatus: async (
    callerMobile: string,
    leadId: string,
    body: {
      lead_status: string;
      comment?: string;
      units?: number;
      reschedule_date?: string;
      visit_type?: string;
      from_datetime?: string;
      to_datetime?: string;
    },
  ): Promise<any> => {
    const res = await api.post(API_ENDPOINTS.leadsStatus(leadId),
      { comment: '', ...body },
      { headers: { 'x-caller-mobile': callerMobile } });
    return res.data;
  },
};

export const marketingService = {
  // The "Direct" referrer, as the CRM's onboarding form gets it: the first mobile in
  // GET /marketing/onboard-config's direct_referrer_mobiles (set by an Admin in the CRM),
  // resolved to its name, role and referral code. Throws when none is configured.
  getDirectReferrer: async (): Promise<{ mobile: string; referral_code: string; name: string; role: string }> => {
    const cfg = await api.get(API_ENDPOINTS.marketingOnboardConfig(), { headers: staffHeaders() });
    const mobiles: string[] = (cfg.data?.config?.direct_referrer_mobiles || []).map(String);
    if (!mobiles.length) throw new Error('No direct referrer is configured');
    const res = await api.get(API_ENDPOINTS.marketingResolveReferrer(mobiles[0]), { headers: staffHeaders() });
    const r = res.data?.referrer || {};
    return { mobile: String(r.mobile || mobiles[0]), referral_code: String(r.referral_code || ''), name: String(r.name || ''), role: String(r.role || '') };
  },
  // Marketing staff list, for the "Attribute to Marketing Person" dropdown on
  // create-order. Any staff caller may call this (not gated to marketing roles).
  // `scope: 'team'` narrows the list to the caller's own marketing subtree —
  // use it wherever the action itself is team-scoped (setOrderLeadBy), so the
  // picker never offers someone the server will reject.
  listStaff: async (callerMobile: string, scope?: 'team'): Promise<any> => {
    const res = await api.get(API_ENDPOINTS.marketingStaffList(scope),
      { headers: { 'x-caller-mobile': callerMobile } });
    return res.data;
  },
  // Farm executives, for the "Farm Visit By" filter on the orders list — same
  // shape/purpose as listStaff, one roster over (farm executives sit outside
  // the marketing reporting tree entirely).
  listFarmStaff: async (callerMobile: string): Promise<any> => {
    const res = await api.get(API_ENDPOINTS.marketingFarmStaffList(),
      { headers: { 'x-caller-mobile': callerMobile } });
    return res.data;
  },
  // Orders placed under the caller (or their team, if a Manager) — powers the
  // "My Orders" screen in a marketing login.
  listOrders: async (callerMobile: string,
                     params?: Record<string, string | number | boolean | undefined>): Promise<any> => {
    const res = await api.get(API_ENDPOINTS.marketingOrdersList(params),
      { headers: { 'x-caller-mobile': callerMobile } });
    return res.data;
  },
  // The orders screen's tiles and executive-wise incentive table, without the
  // page of rows. Same filters as listOrders; payment status never applies.
  getOrdersSummary: async (callerMobile: string,
                           params?: Record<string, string | number | boolean | undefined>): Promise<any> => {
    const res = await api.get(API_ENDPOINTS.marketingOrdersSummary(params),
      { headers: { 'x-caller-mobile': callerMobile } });
    return res.data;
  },
  // One executive-month's orders, paged: exactly the orders that row of
  // executive_incentives counted (execMobile + month), priced as on listOrders.
  listExecutiveOrders: async (callerMobile: string,
                              params: Record<string, string | number | boolean | undefined>): Promise<any> => {
    const res = await api.get(API_ENDPOINTS.marketingExecutiveOrders(params),
      { headers: { 'x-caller-mobile': callerMobile } });
    return res.data;
  },
  // Farm Orders tiles and farm-executive table (visitedOnly is forced on).
  getFarmOrdersSummary: async (callerMobile: string,
                               params?: Record<string, string | number | boolean | undefined>): Promise<any> => {
    const res = await api.get(API_ENDPOINTS.marketingFarmOrdersSummary(params),
      { headers: { 'x-caller-mobile': callerMobile } });
    return res.data;
  },
  // The visited orders one farm_executive_incentives row counts
  // (farmExecMobile + month), every payment status unless one is given.
  listFarmExecutiveOrders: async (callerMobile: string,
                                  params: Record<string, string | number | boolean | undefined>): Promise<any> => {
    const res = await api.get(API_ENDPOINTS.marketingFarmExecutiveOrders(params),
      { headers: { 'x-caller-mobile': callerMobile } });
    return res.data;
  },
  // PAID orders still awaiting incentive approval, with the money each is
  // worth and the queue's true total. Visible to Marketing Manager, Admin and
  // SuperAdmin — the last two also approve a Manager's OWN orders, which no
  // Manager may sign off for themselves.
  listPendingApprovals: async (callerMobile: string,
                               params?: Record<string, string | boolean | undefined>): Promise<any> => {
    const res = await api.get(API_ENDPOINTS.marketingPendingApprovals(params),
      { headers: { 'x-caller-mobile': callerMobile } });
    return res.data;
  },
  // Per-person marketing overview. The API scopes rows by the caller's own
  // position in the hierarchy — an Executive gets one row, a Lead their
  // executives, a Manager their subtree, an Admin everyone.
  getMarketingSummary: async (callerMobile: string,
                              params?: Record<string, string | number | boolean | undefined>): Promise<any> => {
    const res = await api.get(API_ENDPOINTS.marketingSummary(params),
      { headers: { 'x-caller-mobile': callerMobile } });
    return res.data;
  },
  // Targets. Role-scoped read: `targets` is {self, leads: [{...lead,
  // executives: [...], team_* rollup}]} — a Manager gets every Team Lead in
  // scope, a Lead gets one leads-entry (themselves), an Executive gets none.
  // The dashboard's overseer screen groups by that tree client-side rather
  // than reading a separate pre-aggregated field (see TargetsOverviewTab).
  listTargets: async (callerMobile: string,
                      params?: Record<string, string | number | boolean | undefined>): Promise<any> => {
    const res = await api.get(API_ENDPOINTS.targetsOverview(params),
      { headers: { 'x-caller-mobile': callerMobile } });
    return res.data;
  },
  // Marketing Manager or Lead — edits the overall monthly target's window
  // (start_date/end_date) on the shared node. There is only one window per
  // month, so this changes it for every team member linked to it at once.
  updateTargetWindow: async (callerMobile: string,
                             body: { start_date: string; end_date: string; month: string }): Promise<any> => {
    try {
      const res = await api.patch<any>(API_ENDPOINTS.targetsEditWindow(), body,
        { headers: { 'x-caller-mobile': callerMobile } });
      return res.data;
    } catch (error: any) {
      return { error: extractErrorMessage(error) };
    }
  },
  // Marketing Manager or Lead — edits one person's target_value for the
  // given `month` ('YYYY-MM'), overriding the shared monthly default just
  // for them. Never touches anyone else's number. start_date/end_date are
  // not editable here — the window belongs to the overall monthly target,
  // shared by everyone. The target person must already be linked to that
  // month's target (targets are only ever created by the backend's default
  // monthly seeding, never by hand).
  updatePersonTarget: async (callerMobile: string, mobile: string,
                             body: { target_value: number; month: string }): Promise<any> => {
    try {
      const res = await api.patch<any>(API_ENDPOINTS.targetsEditPerson(mobile), body,
        { headers: { 'x-caller-mobile': callerMobile } });
      return res.data;
    } catch (error: any) {
      return { error: extractErrorMessage(error) };
    }
  },
  // Lead overview. Admin/SuperAdmin see every lead regardless of who owns it,
  // with the full status breakdown and an employee-wise filter; marketing roles
  // keep their existing subtree/assigned scoping.
  listLeadsOverview: async (callerMobile: string,
                            params?: Record<string, string | number | boolean | undefined>): Promise<any> => {
    const res = await api.get(API_ENDPOINTS.leadsOverview(params),
      { headers: { 'x-caller-mobile': callerMobile } });
    return res.data;
  },
  // Call log. Admin/SuperAdmin see every call ever logged; a Marketing
  // Manager/Lead sees their subtree's; an Executive sees their own.
  listCalls: async (callerMobile: string,
                    params?: Record<string, string | number | boolean | undefined>): Promise<any> => {
    const res = await api.get(API_ENDPOINTS.callsList(params),
      { headers: { 'x-caller-mobile': callerMobile } });
    return res.data;
  },
  // Checked-in visitors with check-in/out times, duration, executives and
  // guests, plus per-executive totals (GET /leads/checked-in-visits).
  listCheckedInVisits: async (callerMobile: string,
                              params?: Record<string, string | number | boolean | undefined>): Promise<any> => {
    const res = await api.get(API_ENDPOINTS.leadsCheckedInVisits(params),
      { headers: { 'x-caller-mobile': callerMobile } });
    return res.data;
  },
  // Farm/Office visits scoped by caller role (GET /leads/visits)
  listVisits: async (callerMobile: string,
                     params?: Record<string, string | number | boolean | undefined>): Promise<any> => {
    const res = await api.get(API_ENDPOINTS.leadsVisits(params),
      { headers: { 'x-caller-mobile': callerMobile } });
    return res.data;
  },
  // Flat guest list for the Guests tab — flattened server-side (GET
  // /leads/visits/guests), not derived from listVisits' rows on this side.
  listVisitGuests: async (callerMobile: string,
                          params?: Record<string, string | number | boolean | undefined>): Promise<any> => {
    const res = await api.get(API_ENDPOINTS.leadsVisitGuests(params),
      { headers: { 'x-caller-mobile': callerMobile } });
    return res.data;
  },
  // --- Farm staff incentives ------------------------------------------
  // Separate endpoints from the marketing ones above: farm executives are
  // paid on their own progressive ladder under their own node labels, so
  // none of the /incentive-plans calls answer for them.

  // The month's farm ladder. Auto-created server-side on first read, so a
  // month that has never been opened still comes back with its defaults.
  listFarmIncentivePlans: async (callerMobile: string,
                                 params?: { month_year?: string }): Promise<any> => {
    const res = await api.get(API_ENDPOINTS.farmIncentivePlans(params),
      { headers: { 'x-caller-mobile': callerMobile } });
    return res.data;
  },
  // Re-rate the ladder and/or move its window. Bands must be contiguous — a
  // gap pays nothing for the sales inside it — and the window must not
  // overlap another ACTIVE farm plan; the API refuses either with a 400/409.
  // Editing a plan currently in effect creates a new version instead of
  // mutating it — same rule as updateIncentivePlan on the marketing side.
  updateFarmIncentivePlan: async (
    callerMobile: string,
    planId: string,
    plan: {
      name?: string;
      effective_from: string;
      effective_to: string;
      slabs?: Array<{ min_count: number; max_count: number | null; rate: number }>;
    },
  ): Promise<ApiResponse<any>> => {
    try {
      const response = await api.patch(API_ENDPOINTS.farmIncentivePlan(planId), plan, {
        headers: { 'x-caller-mobile': callerMobile, 'Content-Type': 'application/json' },
      });
      if (response.data && (response.data.status === 'error' || response.data.statuscode >= 400)) {
        return { error: response.data.message || 'Failed to update farm incentive plan' };
      }
      return { data: response.data, message: 'Farm incentive plan updated' };
    } catch (error: any) {
      console.error('Error updating farm incentive plan:', error);
      return { error: extractErrorMessage(error) };
    }
  },
  farmIncentiveSummary: async (callerMobile: string,
                               params?: { mobile?: string; month_year?: string }): Promise<any> => {
    const res = await api.get(API_ENDPOINTS.farmIncentivesMy(params),
      { headers: { 'x-caller-mobile': callerMobile } });
    return res.data;
  },
  // Full version lineage for one farm plan — mirrors getIncentivePlanHistory.
  // Editing a plan currently in effect creates a new version rather than
  // mutating it in place, so a month's ladder can change mid-month while
  // still leaving an audit trail of what it looked like when earlier sales
  // in that month were credited.
  getFarmIncentivePlanHistory: async (callerMobile: string, planId: string): Promise<any> => {
    const res = await api.get(API_ENDPOINTS.farmIncentivePlanHistory(planId),
      { headers: { 'x-caller-mobile': callerMobile } });
    return res.data;
  },
  // Ranked farm executives for a month. Everyone on the roster is returned,
  // including a new hire at zero, so the board is never missing someone.
  farmLeaderboard: async (callerMobile: string, params?: { month?: string }): Promise<any> => {
    const res = await api.get(API_ENDPOINTS.farmLeaderboard(params),
      { headers: { 'x-caller-mobile': callerMobile } });
    return res.data;
  },
  // Mirrors setTopPerformer, against its own (:FarmTopPerformer) node label.
  setFarmTopPerformer: async (
    callerMobile: string, execMobile: string, month?: string,
  ): Promise<any> => {
    const res = await api.post(API_ENDPOINTS.farmTopPerformer(),
      { exec_mobile: execMobile, month },
      { headers: { 'x-caller-mobile': callerMobile } });
    return res.data;
  },
  approveIncentive: async (callerMobile: string, orderId: string): Promise<any> => {
    const res = await api.post(API_ENDPOINTS.marketingApproveIncentive(),
      { order_id: orderId }, { headers: { 'x-caller-mobile': callerMobile } });
    return res.data;
  },
  // Every pending incentive of one executive for one month (YYYY-MM), oldest
  // first. Stops at the first refused order; `complete` says whether it did.
  approveIncentivesBulk: async (callerMobile: string, execMobile: string, month: string): Promise<any> => {
    const res = await api.post(API_ENDPOINTS.marketingApproveIncentivesBulk(),
      { exec_mobile: execMobile, month }, { headers: { 'x-caller-mobile': callerMobile } });
    return res.data;
  },
  // The farm side's own approval — independent of approveIncentive above (a
  // lead's visit and its onboarding are two different jobs, approved by
  // whoever reviews each one, on their own timeline).
  approveFarmIncentive: async (callerMobile: string, orderId: string): Promise<any> => {
    const res = await api.post(API_ENDPOINTS.marketingApproveFarmIncentive(),
      { order_id: orderId }, { headers: { 'x-caller-mobile': callerMobile } });
    return res.data;
  },
  // Every pending farm incentive of one farm executive for one month, oldest
  // first (Marketing Manager only). Stops at the first refused order and
  // reports it — same response shape as approveIncentivesBulk.
  approveFarmIncentivesBulk: async (callerMobile: string, execMobile: string, month: string): Promise<any> => {
    const res = await api.post(API_ENDPOINTS.marketingApproveFarmIncentivesBulk(),
      { exec_mobile: execMobile, month }, { headers: { 'x-caller-mobile': callerMobile } });
    return res.data;
  },
  // Create a new incentive plan (Marketing Manager only). Created as DRAFT server-side.
  createIncentivePlan: async (
    callerMobile: string,
    plan: {
      name: string;
      period: 'MONTHLY';
      currency: string;
      slabs: Array<{ lead_type: 'DIRECT' | 'INDIRECT'; min_count: number; max_count: number; payout_type: string; rate: number }>;
      prizes: any[];
      effective_from: string;
      effective_to?: string;
    },
  ): Promise<ApiResponse<any>> => {
    try {
      const response = await api.post(API_ENDPOINTS.incentivePlans(), plan, {
        headers: { 'x-caller-mobile': callerMobile, 'Content-Type': 'application/json' },
      });
      if (response.data && (response.data.status === 'error' || response.data.statuscode >= 400)) {
        return { error: response.data.message || 'Failed to create incentive plan' };
      }
      return { data: response.data, message: 'Incentive plan created' };
    } catch (error: any) {
      console.error('Error creating incentive plan:', error);
      return { error: extractErrorMessage(error) };
    }
  },
  // List incentive plans. Manager sees every status; Lead/Exec are server-scoped
  // to ACTIVE only — this component just passes through whatever the API sends.
  // `month_year` is MM-YYYY. The API narrows to plans in effect during that
  // month, which for an open-ended plan means every month from its start on —
  // so one default plan answers for every later month without being copied.
  listIncentivePlans: async (callerMobile: string, params?: { period?: string; status?: string; month_year?: string }): Promise<any> => {
    const res = await api.get(API_ENDPOINTS.incentivePlans(params),
      { headers: { 'x-caller-mobile': callerMobile } });
    return res.data;
  },
  // Edit a plan's name/dates/slabs (Marketing Manager only). Editing a DRAFT
  // updates it in place; editing an ACTIVE plan creates a new version instead.
  updateIncentivePlan: async (
    callerMobile: string,
    planId: string,
    plan: {
      name: string;
      effective_from: string;
      effective_to: string;
      slabs: Array<{ lead_type: 'DIRECT' | 'INDIRECT'; min_count: number; max_count: number; payout_type: string; rate: number }>;
    },
  ): Promise<ApiResponse<any>> => {
    try {
      const response = await api.patch(API_ENDPOINTS.incentivePlan(planId), plan, {
        headers: { 'x-caller-mobile': callerMobile, 'Content-Type': 'application/json' },
      });
      if (response.data && (response.data.status === 'error' || response.data.statuscode >= 400)) {
        return { error: response.data.message || 'Failed to update incentive plan' };
      }
      return { data: response.data, message: 'Incentive plan updated' };
    } catch (error: any) {
      console.error('Error updating incentive plan:', error);
      return { error: extractErrorMessage(error) };
    }
  },
  // Full version lineage for one plan (Marketing Manager only) — each entry is
  // a version transition (before/after snapshot plus a computed added/removed/
  // changed diff for slabs and prizes).
  getIncentivePlanHistory: async (callerMobile: string, planId: string): Promise<any> => {
    const res = await api.get(API_ENDPOINTS.incentivePlanHistory(planId),
      { headers: { 'x-caller-mobile': callerMobile } });
    return res.data;
  },
  // Monthly sales leaderboard of marketing executives ranked by total paid sales
  getLeaderboard: async (callerMobile: string, month?: string): Promise<any> => {
    const res = await api.get(API_ENDPOINTS.marketingLeaderboard(month),
      { headers: { 'x-caller-mobile': callerMobile } });
    return res.data;
  },
  // Per-platform lead funnel (uploaded -> ordered -> paid) for one window.
  // The same block /marketing/summary returns as `lead_conversion`, served on
  // its own for screens that want the funnel without the whole overview.
  getLeadConversion: async (
    callerMobile: string,
    params?: { month?: string; from_date?: string; end_date?: string; person?: string },
  ): Promise<any> => {
    const res = await api.get(API_ENDPOINTS.marketingLeadConversion(params),
      { headers: { 'x-caller-mobile': callerMobile } });
    return res.data;
  },
  // The month's top-performer pick plus every earlier pick for that month.
  getTopPerformer: async (callerMobile: string, month?: string): Promise<any> => {
    const res = await api.get(API_ENDPOINTS.marketingTopPerformer(month),
      { headers: { 'x-caller-mobile': callerMobile } });
    return res.data;
  },
  // Names the month's top performer. Replaceable: a later pick supersedes this
  // one, and the server keeps both so the audit trail stays intact.
  setTopPerformer: async (
    callerMobile: string, execMobile: string, month?: string,
  ): Promise<any> => {
    const res = await api.post(API_ENDPOINTS.marketingTopPerformer(),
      { exec_mobile: execMobile, month },
      { headers: { 'x-caller-mobile': callerMobile } });
    return res.data;
  },
  // Comprehensive marketing sales performance report (annual & monthly overview)
  getSalesReport: async (callerMobile: string, params?: { month?: string; year?: number; from_date?: string; to_date?: string }): Promise<any> => {
    const res = await api.get(API_ENDPOINTS.marketingSalesReport(params),
      { headers: { 'x-caller-mobile': callerMobile } });
    return res.data;
  },
  // Names the marketing exec behind a PAID order that was created without one.
  // Write-once server-side: attributing an already-attributed order is a 409.
  setOrderLeadBy: async (
    orderId: string, execMobile: string, callerMobile: string,
  ): Promise<ApiResponse<any>> => {
    try {
      const res = await api.post(API_ENDPOINTS.marketingOrderLeadBy(orderId),
        { exec_mobile: execMobile },
        { headers: { 'x-caller-mobile': callerMobile } });
      return { data: res.data, message: 'Lead By updated' };
    } catch (error: any) {
      return { error: extractErrorMessage(error) };
    }
  },
};

export const unitNomineeService = {
  setNominee: async (payload: SetUnitNomineeRequest, callerMobile: string): Promise<ApiResponse<any>> => {
    try {
      // The endpoint only accepts the bulk { assignments: [...] } shape now — a bare
      // single-assignment body gets rejected with a 422 ("assignments: Field required").
      const response = await api.post(API_ENDPOINTS.setUnitNominee(), { assignments: [payload] }, {
        headers: { 'x-caller-mobile': callerMobile, 'Content-Type': 'application/json' },
      });
      if (response.data && (response.data.status === 'error' || response.data.statuscode >= 400)) {
        return { error: response.data.message || 'Failed to save nominee details' };
      }
      return { data: response.data, message: 'Nominee saved' };
    } catch (error: any) {
      return { error: extractErrorMessage(error) };
    }
  },

  // Bulk variant — assigns a (possibly different) nominee to each of several units in one
  // request, instead of one setNominee call per unit. Same endpoint as setNominee, which
  // wraps its own single assignment in this same { assignments: [...] } shape — the
  // endpoint requires it even for one unit.
  setNomineesBulk: async (payload: SetUnitNomineesBulkRequest, callerMobile: string): Promise<ApiResponse<any>> => {
    try {
      const response = await api.post(API_ENDPOINTS.setUnitNominee(), payload, {
        headers: { 'x-caller-mobile': callerMobile, 'Content-Type': 'application/json' },
      });
      if (response.data && (response.data.status === 'error' || response.data.statuscode >= 400)) {
        return { error: response.data.message || 'Failed to save nominee details' };
      }
      return { data: response.data, message: 'Nominees saved' };
    } catch (error: any) {
      return { error: extractErrorMessage(error) };
    }
  },
  getUnitWiseNominees: async (mobile: string, callerMobile: string): Promise<ApiResponse<any>> => {
    try {
      const response = await api.get(API_ENDPOINTS.getUnitWiseNominees(mobile), {
        headers: { 'x-caller-mobile': callerMobile },
      });
      if (response.data && (response.data.status === 'error' || response.data.statuscode >= 400)) {
        return { error: response.data.message || 'Failed to fetch unit-wise nominees' };
      }
      return { data: response.data };
    } catch (error: any) {
      return { error: extractErrorMessage(error) };
    }
  },

  setUnitWiseNominees: async (unitId: string, payload: SetUnitNomineesRequest, callerMobile: string): Promise<ApiResponse<any>> => {
    try {
      const response = await api.post(API_ENDPOINTS.setUnitWiseNominees(unitId), payload, {
        headers: { 'x-caller-mobile': callerMobile, 'Content-Type': 'application/json' },
      });
      if (response.data && (response.data.status === 'error' || response.data.statuscode >= 400)) {
        return { error: response.data.message || 'Failed to save nominee details' };
      }
      return { data: response.data, message: 'Nominees saved' };
    } catch (error: any) {
      return { error: extractErrorMessage(error) };
    }
  },

  addInvestorNominee: async (mobile: string, payload: any, callerMobile: string): Promise<ApiResponse<any>> => {
    try {
      const response = await api.post(API_ENDPOINTS.addInvestorNominee(mobile), payload, {
        headers: { 'x-caller-mobile': callerMobile, 'Content-Type': 'application/json' },
      });
      if (response.data && (response.data.status === 'error' || response.data.statuscode >= 400)) {
        return { error: response.data.message || 'Failed to add nominee to profile' };
      }
      return { data: response.data, message: 'Nominee added to profile' };
    } catch (error: any) {
      return { error: extractErrorMessage(error) };
    }
  },

  saveDraftNominees: async (mobile: string, payload: any, callerMobile: string): Promise<ApiResponse<any>> => {
    try {
      const response = await api.post(API_ENDPOINTS.saveDraftNominees(mobile), payload, {
        headers: { 'x-caller-mobile': callerMobile, 'Content-Type': 'application/json' },
      });
      if (response.data && (response.data.status === 'error' || response.data.statuscode >= 400)) {
        return { error: response.data.message || 'Failed to save draft nominees' };
      }
      return { data: response.data, message: 'Draft nominees saved' };
    } catch (error: any) {
      return { error: extractErrorMessage(error) };
    }
  },

  getDraftNominees: async (mobile: string): Promise<ApiResponse<any>> => {
    try {
      const response = await api.get(API_ENDPOINTS.getDraftNominees(mobile));
      return { data: response.data };
    } catch (error: any) {
      return { error: extractErrorMessage(error) };
    }
  },

  // Edits an already-saved Nominee's own fields in place (PUT, not POST) — every Unit
  // sharing this Nominee sees the change. aadhar_number can't be sent; the backend rejects
  // any attempt to change it (create a new Nominee instead).
  updateNominee: async (nomineeId: string, updates: UpdateNomineeRequest, callerMobile: string): Promise<ApiResponse<any>> => {
    try {
      const response = await api.put(API_ENDPOINTS.updateNominee(nomineeId), updates, {
        headers: { 'x-caller-mobile': callerMobile, 'Content-Type': 'application/json' },
      });
      if (response.data && (response.data.status === 'error' || response.data.statuscode >= 400)) {
        return { error: response.data.message || 'Failed to update nominee details' };
      }
      return { data: response.data, message: 'Nominee updated' };
    } catch (error: any) {
      return { error: extractErrorMessage(error) };
    }
  },

  // Read-back for an order's units + their linked nominee (if any). A 404 (order has no
  // Unit nodes -- e.g. it predates this feature and hasn't been backfilled) is treated as
  // "no units", not an error, so callers can render an empty state instead of a failure.
  getOrderUnits: async (orderId: string): Promise<ApiResponse<{ units: OrderUnit[] }>> => {
    try {
      const response = await api.get(API_ENDPOINTS.getOrderUnits(orderId));
      if (response.data?.statuscode === 404) {
        return { data: { units: [] } };
      }
      if (response.data && (response.data.status === 'error' || response.data.statuscode >= 400)) {
        return { error: response.data.message || 'Failed to load unit/nominee details' };
      }
      return { data: { units: response.data?.units || [] } };
    } catch (error: any) {
      return { error: extractErrorMessage(error) };
    }
  },

  // Existing Nominees already on file for this investor (across their other units) —
  // lets the caller offer "reuse an existing nominee" via nominee_id instead of
  // re-entering the same details for another unit.
  getInvestorNominees: async (mobile: string): Promise<ApiResponse<{ nominees: OrderUnitNominee[] }>> => {
    try {
      const response = await api.get(API_ENDPOINTS.getInvestorNominees(mobile));
      if (response.data && (response.data.status === 'error' || response.data.statuscode >= 400)) {
        return { error: response.data.message || 'Failed to load existing nominees' };
      }
      return { data: { nominees: response.data?.nominees || [] } };
    } catch (error: any) {
      return { error: extractErrorMessage(error) };
    }
  },
};

// Nominee correction requests — employee flags unit nominee fields, the customer fixes
// them through /nominee-correction?token=…. Errors come back as { error, errors? } with
// per-field messages keyed nominee_id -> field.
const correctionCall = async (fn: () => Promise<any>, fallback: string): Promise<ApiResponse<any> & { errors?: any; code?: string; shared_units?: any; details?: any }> => {
  try {
    const { data } = await fn();
    if (data && (data.status === 'error' || data.statuscode >= 400)) {
      return { error: data.message || fallback, errors: data.errors, code: data.code, shared_units: data.shared_units, details: data };
    }
    return { data };
  } catch (error: any) {
    return { error: error.response?.data?.message || error.response?.data?.detail || error.message || fallback };
  }
};

// What was last sent to FarmVest for each of an investor's orders, and what came back
// (routers/purchases.list_farmvest_syncs). Staff only, so it carries x-caller-mobile.
export const farmvestSyncService = {
  list: (mobile: string) =>
    correctionCall(() => api.get(API_ENDPOINTS.farmvestSyncs(mobile), { headers: staffHeaders() }), 'Failed to load FarmVest sync details'),
};

export const nomineeCorrectionService = {
  create: (payload: { investor_mobile: string; unit_id: string; items: { nominee_id: string; fields: string[] }[]; remarks: string }) =>
    correctionCall(() => api.post(API_ENDPOINTS.nomineeCorrections(), payload, { headers: staffHeaders() }), 'Failed to create correction request'),
  list: (investorMobile: string) =>
    correctionCall(() => api.get(API_ENDPOINTS.nomineeCorrections(), { params: { investor_mobile: investorMobile }, headers: staffHeaders() }), 'Failed to load correction requests'),
  cancel: (id: string) =>
    correctionCall(() => api.post(API_ENDPOINTS.cancelNomineeCorrection(id), undefined, { headers: staffHeaders() }), 'Failed to cancel correction request'),
  getByToken: (token: string) =>
    correctionCall(() => api.get(API_ENDPOINTS.nomineeCorrectionByToken(token)), 'Failed to load correction request'),
  submit: (token: string, values: Record<string, Record<string, string | null>>, confirmShared: boolean) =>
    correctionCall(() => api.post(`${API_ENDPOINTS.nomineeCorrectionByToken(token)}/submit`, { values, confirm_shared: confirmShared }), 'Failed to submit corrections'),
};

// Investor KYC / bank / unit-nominee flow. Same { error, errors?, code? } shape as the
// correction calls so pages can show per-field messages from the server.
export interface KycQueueParams {
  status?: string;          // queue tab: filling | in_review | corrections | awaiting_ack | completed
  q?: string;
  kyc?: string;             // comma-separated section statuses
  bank?: string;
  nominees?: string;
  ack?: string;
  pending?: boolean;        // only investors with a section waiting for verification
  details_pending?: boolean; // only investors whose KYC, bank or nominee details are not verified yet
  pending_type?: string;     // kyc | kyc_ack | bank | bank_ack | nominees | nominees_ack
  step?: string;            // onboarding step: kyc | bank_nominees | confirm | complete
  from_date?: string;       // YYYY-MM-DD, inclusive, on the Created date (IST)
  to_date?: string;
  mou?: string;             // given (every unit has the MOU) | not_given, comma-separated
  sort?: 'waiting' | '-waiting' | 'name';
  page?: number;
  page_size?: number;
}

// Staff filling in the investor form on the investor's behalf (assisted entry).
// While on, the investor-form calls name the staff member and ask the server to
// record entries under them (x-kyc-assist); acknowledging stays the investor's own act.
let kycAssist = false;
export const setKycAssist = (on: boolean) => { kycAssist = on; };
const assistHeaders = (): Record<string, string> => (kycAssist ? { ...staffHeaders(), 'x-kyc-assist': '1' } : {});

export const kycFlowService = {
  get: (token: string) =>
    correctionCall(() => api.get(API_ENDPOINTS.kycFlowByToken(token), { headers: assistHeaders() }), 'Failed to load your details'),
  saveKyc: (token: string, values: Record<string, any>) =>
    correctionCall(() => api.put(`${API_ENDPOINTS.kycFlowByToken(token)}/kyc`, { values }, { headers: assistHeaders() }), 'Failed to save KYC details'),
  saveBank: (token: string, values: Record<string, any>) =>
    correctionCall(() => api.put(`${API_ENDPOINTS.kycFlowByToken(token)}/bank`, { values }, { headers: assistHeaders() }), 'Failed to save bank details'),
  saveUnitNominees: (token: string, unitId: string, payload: { mou_type?: string; nominee: Record<string, any> }) =>
    correctionCall(() => api.put(`${API_ENDPOINTS.kycFlowByToken(token)}/units/${encodeURIComponent(unitId)}/nominees`, payload, { headers: assistHeaders() }), 'Failed to save nominees'),
  searchNominee: (token: string, aadhar: string) =>
    correctionCall(() => api.get(`${API_ENDPOINTS.kycFlowByToken(token)}/nominee-search`, { params: { aadhar }, headers: assistHeaders() }), 'Search failed'),
  // section: bank | nominees submits that section alone (unitId: one unit's nominee); omit for KYC (step 1).
  submit: (token: string, section?: 'bank' | 'nominees', unitId?: string) =>
    correctionCall(() => api.post(`${API_ENDPOINTS.kycFlowByToken(token)}/submit`, section ? { section, ...(unitId ? { unit_id: unitId } : {}) } : undefined, { headers: assistHeaders() }), 'Failed to submit'),
  // section: kyc | bank | nominees with that section's version; omit to acknowledge every ready section.
  // unitId: one unit's nominee, with that unit's version (unit.ack.version).
  acknowledge: (token: string, version: string, section?: 'kyc' | 'bank' | 'nominees', unitId?: string) =>
    // Assisted entry is flagged so the server refuses it: acknowledging is the investor's own act.
    correctionCall(() => api.post(`${API_ENDPOINTS.kycFlowByToken(token)}/acknowledge`, { version, confirm: true, ...(section ? { section } : {}), ...(unitId ? { unit_id: unitId } : {}) }, { headers: assistHeaders() }), 'Failed to save acknowledgment'),
  reportIssue: (token: string, remarks: string, section?: string) =>
    correctionCall(() => api.post(`${API_ENDPOINTS.kycFlowByToken(token)}/report-issue`, { remarks, section }), 'Failed to report the issue'),
  // Old /customer-kyc?mobile=… links: trade a WhatsApp OTP for the investor's secure link.
  otpLink: (mobile: string, otp: string) =>
    correctionCall(() => api.post(API_ENDPOINTS.kycFlowOtpLink(), { mobile, otp }), 'Could not verify the OTP'),
  // Staff
  // Reuses the investor's active link; purpose 'ack' also records the version they were asked to confirm.
  // purpose 'ack' records which section version(s) the investor was asked to confirm;
  // section omitted = every section that is verified and waiting for acknowledgment.
  createLink: (investorMobile: string, purpose: 'profile' | 'ack' = 'profile', section?: 'kyc' | 'bank' | 'nominees') =>
    correctionCall(() => api.post(API_ENDPOINTS.kycFlowLinks(), { investor_mobile: investorMobile, purpose, ...(section ? { section } : {}) }, { headers: staffHeaders() }), 'Failed to create the investor link'),
  // Onboarding template for step 1 / 2 / 3: records it as sent (and by whom) and returns
  // its text ({link} left for the caller) plus the investor's link path. Steps 1 and 2
  // are sent on WhatsApp by the server (via: 'whatsapp'); step 3 is copied (via: 'copied').
  sendStepMessage: (investorMobile: string, step: 1 | 2 | 3) =>
    correctionCall(() => api.post(API_ENDPOINTS.kycFlowStepMessage(investorMobile), { step }, { headers: staffHeaders() }), 'Failed to prepare the message'),
  // ank_verify WhatsApp: the investor's link to acknowledge one verified section — KYC,
  // bank, or one unit's nominee (section 'nominees' + unitId). Returns the fresh flow.
  sendAckMessage: (investorMobile: string, section: 'kyc' | 'bank' | 'nominees', unitId?: string) =>
    correctionCall(() => api.post(API_ENDPOINTS.kycFlowAckMessage(investorMobile), { section, ...(unitId ? { unit_id: unitId } : {}) }, { headers: staffHeaders() }), 'Failed to send the acknowledgment link'),
  // ank_nominee WhatsApp: the investor's link to add nominees for a new order's units.
  sendOrderNomineeMessage: (investorMobile: string, orderId: string) =>
    correctionCall(() => api.post(API_ENDPOINTS.kycFlowOrderNomineeMessage(investorMobile, orderId), undefined, { headers: staffHeaders() }), 'Failed to send the nominee link'),
  staffView: (investorMobile: string) =>
    correctionCall(() => api.get(API_ENDPOINTS.kycFlowStaff(investorMobile), { headers: staffHeaders() }), 'Failed to load the investor flow'),
  queue: (params: KycQueueParams = {}) =>
    correctionCall(() => api.get(API_ENDPOINTS.kycFlowQueue(), {
      // drop empty filters so the URL stays clean
      params: Object.fromEntries(Object.entries(params).filter(([, v]) => v !== undefined && v !== '' && v !== false)),
      headers: staffHeaders(),
    }), 'Failed to load the verification queue'),
  // proof: what the client sent asking for the change — required once the field is verified.
  staffEdit: (investorMobile: string, field: string, value: any, reason: string, proof?: { url: string; source: string }) =>
    correctionCall(() => api.post(API_ENDPOINTS.kycFlowStaffEdit(investorMobile), {
      field, value, reason, ...(proof ? { proof_url: proof.url, proof_source: proof.source } : {}),
    }, { headers: staffHeaders() }), 'Failed to update the field'),
  // One field of a unit's nominee; same proof rules as staffEdit.
  // confirmShared: the nominee is shared with other investors (code NOMINEE_SHARED) and staff agreed to change it for all.
  staffEditNominee: (investorMobile: string, unitId: string, nomineeId: string, field: string, value: any, reason: string, proof?: { url: string; source: string }, confirmShared = false) =>
    correctionCall(() => api.post(API_ENDPOINTS.kycFlowNomineeEdit(investorMobile, unitId, nomineeId), {
      field, value, reason, ...(proof ? { proof_url: proof.url, proof_source: proof.source } : {}),
      ...(confirmShared ? { confirm_shared: true } : {}),
    }, { headers: staffHeaders() }), 'Failed to update the nominee'),
  requestCorrection: (investorMobile: string, fields: string[], remarks: string) =>
    correctionCall(() => api.post(API_ENDPOINTS.kycFlowCorrections(investorMobile), { fields, remarks }, { headers: staffHeaders() }), 'Failed to request correction'),
};

export const receiptService = {
  generateReceipt: async (
    orderId: string,
    txId: string,
    callerMobile: string
  ): Promise<ApiResponse<{ pdf_url: string }>> => {
    try {
      const response = await api.post(
        API_ENDPOINTS.generateReceipt(),
        { order_id: orderId, tx_id: txId },
        { headers: { 'x-caller-mobile': callerMobile, 'Content-Type': 'application/json' } }
      );
      if (response.data && (response.data.status === 'error' || response.data.statuscode >= 400)) {
        return { error: response.data.message || 'Failed to generate receipt' };
      }
      return { data: response.data, message: response.data?.message || 'Receipt generated' };
    } catch (error: any) {
      return { error: extractErrorMessage(error) };
    }
  },
  sendReceipt: async (
    orderId: string,
    txId: string,
    callerMobile: string
  ): Promise<ApiResponse<{ pdf_url: string; sent_at: string; sent_by_name: string }>> => {
    try {
      const response = await api.post(
        API_ENDPOINTS.sendReceipt(),
        { order_id: orderId, tx_id: txId },
        { headers: { 'x-caller-mobile': callerMobile, 'Content-Type': 'application/json' } }
      );
      if (response.data && (response.data.status === 'error' || response.data.statuscode >= 400)) {
        return { error: response.data.message || 'Failed to send receipt' };
      }
      return { data: response.data, message: response.data?.message || 'Receipt sent' };
    } catch (error: any) {
      return { error: extractErrorMessage(error) };
    }
  },
};

export const farmvestService = {
  syncFarmvest: async (mobile: string, callerMobile?: string): Promise<ApiResponse<any>> => {
    try {
      const response = await api.post(
        API_ENDPOINTS.syncFarmvest(mobile),
        {},
        {
          headers: callerMobile ? { 'x-caller-mobile': callerMobile } : undefined,
        }
      );
      if (response.data && (response.data.status === 'error' || response.data.statuscode >= 400)) {
        return { error: response.data.message || response.data.detail || 'Failed to sync to FarmVest' };
      }
      return {
        data: response.data,
        message: response.data?.message || response.data?.detail || 'Synced to FarmVest successfully',
      };
    } catch (error: any) {
      return { error: extractErrorMessage(error) };
    }
  },
};

export const calculatorSettingsService = {
  getSettings: async (): Promise<any> => {
    try {
      const response = await api.get<any>(API_ENDPOINTS.getCalculatorSettings());
      return response.data;
    } catch (error) {
      console.error('Error fetching calculator settings:', error);
      throw error;
    }
  },

  // The latest created model (show_current_model), which the calculators display.
  // It may not be active yet -- that happens through activateModel.
  getCurrentSettings: async (): Promise<any> => {
    try {
      const response = await api.get<any>(API_ENDPOINTS.getCurrentCalculatorSettings());
      return response.data;
    } catch (error) {
      console.error('Error fetching current calculator settings:', error);
      throw error;
    }
  },

  getSettingsByVersion: async (version: number): Promise<any> => {
    try {
      const response = await api.get<any>(API_ENDPOINTS.getCalculatorSettingsByVersion(version));
      return response.data;
    } catch (error) {
      console.error('Error fetching calculator settings by version:', error);
      throw error;
    }
  },
  
  getHistory: async (): Promise<any> => {
    try {
      const response = await api.get<any>(API_ENDPOINTS.getCalculatorSettingsHistory());
      return response.data;
    } catch (error) {
      console.error('Error fetching calculator settings history:', error);
      throw error;
    }
  },

  requestOtp: async (): Promise<any> => {
    try {
      const response = await api.post(API_ENDPOINTS.requestCalculatorSettingsOtp());
      return response.data;
    } catch (error: any) {
      console.error('Error requesting OTP:', error);
      throw error;
    }
  },

  updateSettings: async (settings: any, otp: string): Promise<any> => {
    try {
      const response = await api.post(API_ENDPOINTS.updateCalculatorSettings(), { settings, otp });
      return response.data;
    } catch (error: any) {
      console.error('Error updating calculator settings:', error);
      throw error;
    }
  },

  // Make an existing model the active one. OTP comes from requestOtp().
  // One API for both switches: target 'active' (orders are priced under it) or 'current'
  // (what the calculators display; new models become current on creation).
  activateModel: async (modelVersion: number, otp: string, target: 'active' | 'current' = 'active'): Promise<any> => {
    try {
      const response = await api.post(API_ENDPOINTS.activateCalculatorSettingsModel(), {
        model_version: modelVersion,
        otp,
        target,
      });
      return response.data;
    } catch (error: any) {
      console.error('Error activating calculator model:', error);
      throw error;
    }
  },

};

export const unitTransferService = {
  getTransferDetails: async (unitNumber: string, callerMobile?: string): Promise<ApiResponse<any>> => {
    try {
      const headers: Record<string, string> = {};
      if (callerMobile) headers['x-caller-mobile'] = callerMobile;
      const response = await api.get<any>(API_ENDPOINTS.unitTransferDetails(unitNumber), { headers });
      if (response.data && (response.data.status === 'error' || response.data.statuscode >= 400)) {
        return { error: response.data.message || 'Failed to fetch unit transfer details' };
      }
      return { data: response.data };
    } catch (error: any) {
      return { error: extractErrorMessage(error) };
    }
  },
};


// ── Cashier roster (backend routers/cashiers.py) ─────────────────────────────
// Every /cashiers endpoint answers in one envelope -- {statuscode, status, ...}
// -- including the 400s and 403s its helpers raise, so one check here covers
// every failure mode and the caller always gets {data} or {error}.

const cashierEnvelope = <T,>(data: any, fallback: string): ApiResponse<T> => (
  data && (data.status === 'error' || data.statuscode >= 400)
    ? { error: data.message || fallback }
    : { data }
);

export const cashierService = {
  // `activeOnly` is what a payment form should send: a deactivated cashier must
  // not be selectable for a new collection, while the roster screen still shows
  // them so past collections stay explicable.
  getCashiers: async (params?: { search?: string; activeOnly?: boolean; callerMobile?: string }):
    Promise<ApiResponse<{ total: number; cashiers: Cashier[]; can_manage: boolean }>> => {
    try {
      const headers: Record<string, string> = {};
      if (params?.callerMobile) headers['x-admin-mobile'] = params.callerMobile;
      const response = await api.get<any>(
        API_ENDPOINTS.getCashiers({ search: params?.search, activeOnly: params?.activeOnly }),
        { headers },
      );
      return cashierEnvelope(response.data, 'Failed to fetch cashiers');
    } catch (error: any) {
      return { error: extractErrorMessage(error) };
    }
  },

  addCashier: async (cashierName: string, cashierPhoneNumber: string, callerMobile?: string):
    Promise<ApiResponse<{ cashier: Cashier }>> => {
    try {
      const headers: Record<string, string> = {};
      if (callerMobile) headers['x-admin-mobile'] = callerMobile;
      const response = await api.post<any>(
        API_ENDPOINTS.addCashier(), { cashierName, cashierPhoneNumber }, { headers },
      );
      return cashierEnvelope(response.data, 'Failed to add cashier');
    } catch (error: any) {
      return { error: extractErrorMessage(error) };
    }
  },

  // Only the fields passed are sent, matching the backend's sparse patch --
  // correcting a phone number cannot blank the name.
  updateCashier: async (
    cashierId: string,
    changes: { cashierName?: string; cashierPhoneNumber?: string; isActive?: boolean },
    callerMobile?: string,
  ): Promise<ApiResponse<{ cashier: Cashier }>> => {
    try {
      const headers: Record<string, string> = {};
      if (callerMobile) headers['x-admin-mobile'] = callerMobile;
      const response = await api.patch<any>(
        API_ENDPOINTS.updateCashier(cashierId), changes, { headers },
      );
      return cashierEnvelope(response.data, 'Failed to update cashier');
    } catch (error: any) {
      return { error: extractErrorMessage(error) };
    }
  },

  // A soft delete: the cashier stops being selectable, and the payments that
  // already name them keep reading. Reverse it with updateCashier({isActive: true}).
  deactivateCashier: async (cashierId: string, callerMobile?: string):
    Promise<ApiResponse<{ cashier: Cashier }>> => {
    try {
      const headers: Record<string, string> = {};
      if (callerMobile) headers['x-admin-mobile'] = callerMobile;
      const response = await api.delete<any>(
        API_ENDPOINTS.deactivateCashier(cashierId), { headers },
      );
      return cashierEnvelope(response.data, 'Failed to deactivate cashier');
    } catch (error: any) {
      return { error: extractErrorMessage(error) };
    }
  },
};
